<template>
  <div>
    <FavoriteBinance ref="favoriteBinance" @period-selected="handleSearch" @refresh-clicked="handleRefresh" @handle-fetchCoins="fetchCoins"
      @metrics-changed="handleMetrics" @single-coin-search="handleSingleCoinSearch"
      @clear-single-coin-search="handleClearSingleCoinSearch" @tab-changed="handleTabChange"
      @coins-loaded="handleCoinsLoaded" @inflow-days-changed="handleInflowDaysChanged"
      :selectedMetrics="selectedMetrics" />

    <!-- Single Coin Search Charts -->
    <template v-if="singleCoinSearchActive && effectiveSelection.length">
      <div class="chart-grid single-coin-search-chart">
        <div class="chart-header text-center">
          <h3>
            {{ getCoinDisplayName(singleSearchCoin) }}
            <span v-if="singleCoinMetrics.monitoringFlag === 'Y'" class="monitoring-badge">
              Monitoring
            </span>
          </h3>
          <div class="metrics-info">
            <a v-if="singleCoinMetrics.cmcUrl" :href="singleCoinMetrics.cmcUrl" target="_blank"
              rel="noopener noreferrer" class="metric-item metric-item-link metric-item-stacked">
              <span class="metric-row">
                <strong>Mc:</strong>
                <span>{{ formatCurrency(singleCoinMetrics.marketcap) }}</span>
              </span>
              <span class="metric-row">
                <strong>FDV:</strong>
                <span>{{ formatCurrency(singleCoinMetrics.fdv) }}</span>
              </span>
            </a>
            <div v-else class="metric-item metric-item-stacked">
              <span class="metric-row">
                <strong>Mc:</strong>
                <span>{{ formatCurrency(singleCoinMetrics.marketcap) }}</span>
              </span>
              <span class="metric-row">
                <strong>FDV:</strong>
                <span>{{ formatCurrency(singleCoinMetrics.fdv) }}</span>
              </span>
            </div>
            <div class="metric-item metric-item-stacked">
              <span class="metric-row">
                <strong>Vol/Mc:</strong>
                <span class="vol-percent">
                  <span v-if="isSingleCoinMetricsLoading" class="loading-spinner"></span>
                  <span v-else>{{ singleCoinMetrics.volMktCapPercent != null ? singleCoinMetrics.volMktCapPercent.toFixed(2) + '%' : '-' }}</span>
                </span>
              </span>
              <span class="metric-row">
                <strong>OI/Mc:</strong>
                <span class="vol-percent">
                  <span v-if="isSingleCoinMetricsLoading" class="loading-spinner"></span>
                  <span v-else>{{ singleCoinMetrics.oiMktCapPercent != null ? singleCoinMetrics.oiMktCapPercent.toFixed(2) + '%' : '-' }}</span>
                </span>
              </span>
            </div>
            <a :href="getLiquidationHeatmapUrl(singleSearchCoin)" target="_blank" rel="noopener noreferrer"
              class="heatmap-btn">
              Lq Hmap
            </a>
            <router-link :to="{ name: 'Orderbook', params: { symbol: singleSearchCoin } }"
              class="heatmap-btn" title="View Orderbook">
              <i class="fa-solid fa-book-open"></i> Orderbook
            </router-link>
            <!-- XLink for single coin -->
            <a v-if="singleCoinMetrics.xLink" :href="singleCoinMetrics.xLink" target="_blank" rel="noopener noreferrer"
              class="w-10 h-10 flex items-center justify-center text-white rounded-full
         bg-gradient-to-br from-[#667eea] to-[#764ba2]
        border-[#667eea]
         shadow-[0_4px_12px_rgba(102,126,234,0.2)]
         transition hover:scale-110 hover:brightness-110">
              <i class="fa-brands fa-x-twitter text-[18px]"></i>
            </a>
          </div>
        </div>

        <div class="chart-box">
          <div class="chart-container first-chart"
            v-if="effectiveSelection.includes('openInterest') && (singleSearchChartDataSets?.length > 0)">
            <canvas id="single-search-chart-0" key="single-search-chart-0" ref="single-search-chart-0"></canvas>
          </div>

          <div class="chart-container second-chart"
            v-if="effectiveSelection.includes('longShort') && (singleSearchSecondChartDataSets?.length > 0)">
            <canvas id="single-search-second-chart-0" key="single-search-second-chart-0"
              ref="single-search-second-chart-0"></canvas>
          </div>

          <div class="chart-container third-chart"
            v-if="effectiveSelection.includes('longShort') && (singleSearchThirdChartDataSets?.length > 0)">
            <canvas id="single-search-third-chart-0" key="single-search-third-chart-0"
              ref="single-search-third-chart-0"></canvas>
          </div>

          <div class="chart-container four-chart"
            v-if="effectiveSelection.includes('longShort') && (singleSearchFourChartDataSets?.length > 0)">
            <canvas id="single-search-four-chart-0" key="single-search-four-chart-0"
              ref="single-search-four-chart-0"></canvas>
          </div>

          <div class="chart-container five-chart"
            v-if="effectiveSelection.includes('inflow30d') && (singleSearchFiveChartDataSets?.dataSet?.length > 0)">
            <canvas id="single-search-five-chart-0" key="single-search-five-chart-0"
              ref="single-search-five-chart-0"></canvas>
          </div>

          <div class="chart-container six-chart"
            v-if="effectiveSelection.includes('inflow24h') && (singleSearchSixChartDataSets?.length > 0)">
            <canvas id="single-search-six-chart-0" key="single-search-six-chart-0"
              ref="single-search-six-chart-0"></canvas>
          </div>

          <div class="chart-container seven-chart"
            v-if="effectiveSelection.includes('fundingRate') && (singleSearchSevenChartDataSets?.length > 0)">
            <canvas id="single-search-seven-chart-0" key="single-search-seven-chart-0"
              ref="single-search-seven-chart-0"></canvas>
          </div>

          <div class="chart-container eight-chart"
            v-if="effectiveSelection.includes('oiMarketcapRatio') && (singleSearchEightChartDataSets?.length > 0)">
            <canvas id="single-search-eight-chart-0" key="single-search-eight-chart-0"
              ref="single-search-eight-chart-0"></canvas>
          </div>


        </div>
      </div>
    </template>

    <!-- Favorite Coins Charts - Only show when NOT in single coin search mode -->
    <template v-if="!singleCoinSearchActive && effectiveSelection.length">
      <div v-for="index in totalChart" :key="'chart-grid-' + index" class="chart-grid">
        <div class="chart-header text-center">
          <h3>
            Biểu đồ
            {{ getCoinDisplayName(arrayCoin[index - 1]) }}
            <span v-if="typeof arrayCoin[index - 1] === 'object' && arrayCoin[index - 1]?.monitoringFlag === 'Y'"
              class="monitoring-badge">
              Monitoring
            </span>
          </h3>
          <div class="metrics-info">
            <a v-if="getCoinMetrics(arrayCoin[index - 1]).cmcUrl" :href="getCoinMetrics(arrayCoin[index - 1]).cmcUrl"
              target="_blank" rel="noopener noreferrer" class="metric-item metric-item-link metric-item-stacked">
              <span class="metric-row">
                <strong>Mc:</strong>
                <span>{{ formatCurrency(getCoinMetrics(arrayCoin[index - 1]).marketcap) }}</span>
              </span>
              <span class="metric-row">
                <strong>FDV:</strong>
                <span>{{ formatCurrency(getCoinMetrics(arrayCoin[index - 1]).fdv) }}</span>
              </span>
            </a>
            <div v-else class="metric-item metric-item-stacked">
              <span class="metric-row">
                <strong>Mc:</strong>
                <span>{{ formatCurrency(getCoinMetrics(arrayCoin[index - 1]).marketcap) }}</span>
              </span>
              <span class="metric-row">
                <strong>FDV:</strong>
                <span>{{ formatCurrency(getCoinMetrics(arrayCoin[index - 1]).fdv) }}</span>
              </span>
            </div>
            <div class="metric-item metric-item-stacked">
              <span class="metric-row">
                <strong>Vol/Mc:</strong>
                <span class="vol-percent">
                  <span v-if="isCoinMetricsLoading" class="loading-spinner"></span>
                  <span v-else>{{ getCoinMetrics(arrayCoin[index - 1]).volMktCapPercent != null ? getCoinMetrics(arrayCoin[index - 1]).volMktCapPercent.toFixed(2) + '%' : '-' }}</span>
                </span>
              </span>
              <span class="metric-row">
                <strong>OI/Mc:</strong>
                <span class="vol-percent">
                  <span v-if="isCoinMetricsLoading" class="loading-spinner"></span>
                  <span v-else>{{ getCoinMetrics(arrayCoin[index - 1]).oiMktCapPercent != null ? getCoinMetrics(arrayCoin[index - 1]).oiMktCapPercent.toFixed(2) + '%' : '-' }}</span>
                </span>
              </span>
            </div>
            <a :href="getLiquidationHeatmapUrl(arrayCoin[index - 1])" target="_blank" rel="noopener noreferrer"
              class="heatmap-btn">
              Lq Hmap
            </a>

            <!-- XLink for multi coin -->
            <a v-if="getCoinMetrics(arrayCoin[index - 1]).xLink" :href="getCoinMetrics(arrayCoin[index - 1]).xLink"
              target="_blank" rel="noopener noreferrer" class="w-10 h-10 flex items-center justify-center text-white rounded-full
         bg-gradient-to-br from-[#667eea] to-[#764ba2]
        border-[#667eea]
         shadow-[0_4px_12px_rgba(102,126,234,0.2)]
         transition hover:scale-110 hover:brightness-110">
              <i class="fa-brands fa-x-twitter text-[18px]"></i>
            </a>
          </div>
        </div>

        <div class="chart-box">
          <div class="chart-container first-chart"
            v-if="effectiveSelection.includes('openInterest') && isValidData(chartDataSets, index)">
            <canvas :id="chartIds[index - 1]" :key="chartIds[index - 1]" :ref="'chart' + (index - 1)"></canvas>
          </div>

          <div class="chart-container second-chart"
            v-if="effectiveSelection.includes('longShort') && isValidData(secondChartDataSets, index)">
            <canvas :id="secondChartIds[index - 1]" :key="secondChartIds[index - 1]"
              :ref="'second-chart' + (index - 1)"></canvas>
          </div>

          <div class="chart-container third-chart"
            v-if="effectiveSelection.includes('longShort') && isValidData(thirdChartDataSets, index)">
            <canvas :id="thirdChartIds[index - 1]" :key="thirdChartIds[index - 1]"
              :ref="'third-chart' + (index - 1)"></canvas>
          </div>

          <div class="chart-container four-chart"
            v-if="effectiveSelection.includes('longShort') && isValidData(fourChartDataSets, index)">
            <canvas :id="fourChartIds[index - 1]" :key="fourChartIds[index - 1]"
              :ref="'four-chart' + (index - 1)"></canvas>
          </div>

          <div class="chart-container five-chart" v-if="effectiveSelection.includes('inflow30d') &&
            isValidData(fiveChartDataSets, index)">
            <canvas :id="fiveChartIds[index - 1]" :key="fiveChartIds[index - 1]"
              :ref="'five-chart' + (index - 1)"></canvas>
          </div>

          <div class="chart-container six-chart" v-if="effectiveSelection.includes('inflow24h') &&
            isValidData(sixChartDataSets, index)">
            <canvas :id="sixChartIds[index - 1]" :key="sixChartIds[index - 1]"
              :ref="'six-chart' + (index - 1)"></canvas>
          </div>

          <div class="chart-container seven-chart"
            v-if="effectiveSelection.includes('fundingRate') && isValidData(sevenChartDataSets, index)">
            <canvas :id="sevenChartIds[index - 1]" :key="sevenChartIds[index - 1]"
              :ref="'seven-chart' + (index - 1)"></canvas>
          </div>


        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  TimeScale,
  BarController,
  BarElement,
  LinearScale,
  LineElement,
  PointElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler,
  SubTitle,
} from "chart.js";
import "chartjs-adapter-date-fns";
import '@/assets/css/binance.css'
import "vue-select/dist/vue-select.css";
import { formatCurrency } from '@/utils/currency';
import { niceMinMax, fmtComma, formatNumber } from '@/utils/number';
import { isValidData, filterZeroData, isAllZeroArray } from '@/utils/array';
import { shadowLine, stripeBg } from '@/plugins/chart';
import { ALL_METRICS } from '@/constants/metrics';
import BinanceService from "@/services/BinanceService";
import ChartDataLabels from "chartjs-plugin-datalabels";
import FavoriteBinance from './favorite/FavoriteBinance.vue'
import MetricService from "@/services/MetricService";
ChartJS.register(
  CategoryScale,
  SubTitle,
  TimeScale,
  BarController,
  BarElement,
  LinearScale,
  LineElement,
  PointElement,
  LineController,
  ChartDataLabels,
  Title,
  Tooltip,
  Legend,
  Filler
);

ChartJS.register(shadowLine)
export default {
  name: "BinanceCoin",
  components: {
    FavoriteBinance
  },
  data() {
    return {
      selectedCoin: null,
      chartIds: Array.from({ length: 30 }, (_, i) => `chart-${i}`),
      secondChartIds: Array.from({ length: 30 }, (_, i) => `second-chart-${i}`),
      thirdChartIds: Array.from({ length: 30 }, (_, i) => `third-chart-${i}`),
      fourChartIds: Array.from({ length: 30 }, (_, i) => `four-chart-${i}`),
      fiveChartIds: Array.from({ length: 30 }, (_, i) => `five-chart-${i}`),
      sixChartIds: Array.from({ length: 30 }, (_, i) => `six-chart-${i}`),
      sevenChartIds: Array.from({ length: 30 }, (_, i) => `seven-chart-${i}`),
      eightChartIds: Array.from({ length: 30 }, (_, i) => `eight-chart-${i}`),
      coinNames: Array(20).fill(""),
      periods: "15m",
      selectedMetrics: ['inflow30d', 'inflow24h', 'fundingRate'],
      initialMetrics: ['inflow30d', 'inflow24h', 'fundingRate'],
      inflowDays: 5,
      charts: [],
      secondCharts: [],
      thirdCharts: [],
      fourCharts: [],
      fiveCharts: [],
      sixCharts: [],
      sevenCharts: [],
      eightCharts: [],
      chartDataSets: [],
      secondChartDataSets: [],
      thirdChartDataSets: [],
      fourChartDataSets: [],
      fiveChartDataSets: [],
      sixChartDataSets: [],
      sevenChartDataSets: [],
      eightChartDataSets: [],

      loading: false,
      coins: [],
      totalChart: 0,
      arrayCoin: [],
      displayedCoins: [],
      exceptionSymbols: new Set(["1000CAT", "1000CATUSDT", "1000CHEEMS", "1000CHEEMSUSDT", "1000SATS", "1000SATSUSDT"]),
      coinMetricsData: {}, // Lưu marketcap và fdv cho từng coin
      // Tab tracking
      currentTab: 'favorite', // Track current active tab
      // Single coin search data
      singleCoinSearchActive: false,
      singleSearchCoin: "",
      singleSearchPeriod: "",
      singleCoinMetrics: { marketcap: null, fdv: null, volMktCapPercent: null, oiMktCapPercent: null, cmcUrl: null, xLink: null, monitoringFlag: null },
      isSingleCoinMetricsLoading: false,
      singleSearchChartDataSets: [],
      singleSearchSecondChartDataSets: [],
      singleSearchThirdChartDataSets: [],
      singleSearchFourChartDataSets: [],
      singleSearchFiveChartDataSets: [],
      singleSearchSixChartDataSets: [],
      singleSearchSevenChartDataSets: [],
      singleSearchEightChartDataSets: [],

      singleSearchCharts: [],
      singleSearchSecondCharts: [],
      singleSearchThirdCharts: [],
      singleSearchFourCharts: [],
      singleSearchFiveCharts: [],
      singleSearchSixCharts: [],
      singleSearchSevenCharts: [],
      singleSearchEightCharts: [],
      pollingTimer: null,
      metricsPollingTimer: null,
      isCoinMetricsLoading: false
    };
  },

  computed: {
    effectiveSelection() {
      const selected = (this.selectedMetrics && this.selectedMetrics.length > 0)
        ? this.selectedMetrics
        : ALL_METRICS;
      return selected.filter(m => ALL_METRICS.includes(m));
    }
  },
  async created() {
    // Không gọi fetchCoins ở đây nữa vì con (FavoriteBinance) sẽ gọi và báo lên qua @coins-loaded
    // load từ localStorage khi component tạo
    const res = await MetricService.getMetrics();
    this.selectedMetrics = res.metrics;
  },
  async mounted() {
    // Check if there is a coinName in the route params
    if (this.$route.params.coinName) {
      // Ensure coins are available for handleSingleCoinSearch logic
      if (this.coins.length === 0) {
        await this.fetchCoins();
      }

      await this.handleSingleCoinSearch({
        coin: { coinName: this.$route.params.coinName },
        period: this.periods
      });

      // Collapse the dropdown list in the child component
      if (this.$refs.favoriteBinance) {
        this.$refs.favoriteBinance.showDropdown = false;
      }
    }

    this.initPolling();

    // Listen to visibility changes to recreate charts when browser tab is re-activated
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        console.log("👁️ Tab active again, recreating charts...");
        if (this.singleCoinSearchActive) {
          this.createSingleCoinCharts();
        } else {
          this.createCharts();
        }
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  },


  watch: {
    // Select one coin and show chart
    '$route.params.coinName': {
    async handler(newVal) {
      console.log("Route changed:", newVal);
      if (newVal) {
        if (this.coins.length === 0) {
          await this.fetchCoins();
        }

        this.singleCoinSearchActive = true;

        await this.handleSingleCoinSearch({
          coin: { coinName: newVal },
          period: this.periods
        });

        if (this.$refs.favoriteBinance) {
          this.$refs.favoriteBinance.showDropdown = false;
        }
      }

      // CASE 2: Back /binance → reset with list
      else {
        console.log("Back to list mode");

        this.singleCoinSearchActive = false;

        // clear chart data
        this.singleSearchCoin = "";
        this.singleSearchChartDataSets = [];
        this.singleSearchSecondChartDataSets = [];
        this.singleSearchThirdChartDataSets = [];
        this.singleSearchFourChartDataSets = [];
        this.singleSearchFiveChartDataSets = [];
        this.singleSearchSixChartDataSets = [];
        this.singleSearchSevenChartDataSets = [];
        this.singleSearchEightChartDataSets = [];

        // show dropdown list again
        if (this.$refs.favoriteBinance) {
          this.$refs.favoriteBinance.showDropdown = true;
        }
      }
    }
  }
  },

  methods: {


    getCoinName(coinItem) {
      if (typeof coinItem === "string") {
        return coinItem;
      }
      return coinItem?.coinName || "BTC";
    },

    isValidData,

    getCoinMetrics(coinItem) {
      const coinName = this.getCoinName(coinItem).replace("USDT", "");
      const metrics = this.coinMetricsData[coinName] || { marketcap: null, fdv: null, volMktCapPercent: null, oiMktCapPercent: null, cmcUrl: null, xLink: null };
      // console.log(`🔍 Getting metrics for ${coinName}:`, metrics);
      return metrics;
    },

    formatCurrency,

    getLiquidationHeatmapUrl(coinItem) {
      const coinName = this.getCoinName(coinItem).replace("USDT", "");
      return `https://www.coinglass.com/pro/futures/LiquidationHeatMap?coin=${coinName}&type=symbol`;
    },
    normalizeCoinName(coinName) {
      if (!coinName) return "";
      const upper = coinName.toUpperCase();
      if (upper === "1000CAT" || upper === "1000CATUSDT") {
        return coinName;
      }
      if (this.exceptionSymbols.has(coinName)) {
        return coinName;
      }
      return coinName.replace(/^\d+/, '');
    },
    hasSpotMarket(coinName) {
      if (!coinName) return false;
      const clean = String(coinName).replace(/USDT$/i, "").toUpperCase();
      const normalized = this.normalizeCoinName(clean).toUpperCase();
      const coinList = (this.coins && this.coins.length > 0)
        ? this.coins
        : ((this.$refs.favoriteBinance?.allDbCoins && this.$refs.favoriteBinance.allDbCoins.length > 0)
          ? this.$refs.favoriteBinance.allDbCoins
          : (this.$refs.favoriteBinance?.coins || []));

      if (coinList.length === 0) {
        return false;
      }

      // 1. Kiểm tra trực tiếp symbol
      const directCoin = coinList.find((c) => {
        const name = String(c?.coinName || "").replace(/USDT$/i, "").toUpperCase();
        return name === clean;
      });
      if (directCoin) {
        return String(directCoin.type || "").toLowerCase().includes("spot");
      }

      // 2. Nếu là coin Futures có tiền tố (ví dụ 1000PEPE -> PEPE), kiểm tra token gốc có Spot không
      if (normalized !== clean) {
        const baseCoin = coinList.find((c) => {
          const name = String(c?.coinName || "").replace(/USDT$/i, "").toUpperCase();
          return name === normalized;
        });
        if (baseCoin) {
          return String(baseCoin.type || "").toLowerCase().includes("spot");
        }
      }

      return false;
    },
    handleCoinsLoaded(payload) {
      const data = Array.isArray(payload) ? payload : (payload?.coins || []);
      const fromFilter = Array.isArray(payload) ? false : Boolean(payload?.fromFilter);
      console.log("📦 Parent received coins-loaded event from child", { count: data.length, fromFilter });
      this.processCoinsData(data, fromFilter);
    },

    getCoinDisplayName(coin) {
      if (!coin) return "";
      const name = typeof coin === "object" ? coin.coinName : coin;
      const baseName = name ? name.replace("USDT", "") : "";
      const coinObj = this.coins.find(
        (c) =>
          c.coinName === name ||
          c.coinName === name + "USDT" ||
          c.coinName + "USDT" === name
      );
      let cmcName = coinObj ? coinObj.coinNameCmc : (typeof coin === "object" ? coin.coinNameCmc : null);
      if (baseName.toUpperCase() === "1000CAT" && (!cmcName || cmcName.includes("Caterpillar"))) {
        cmcName = "Simon's Cat";
      }
      if (cmcName) {
        return `${baseName} (${cmcName})`;
      }
      return baseName;
    },

    processCoinsData(data, fromFilter = false) {
      this.coins = data.map((coin, index) => ({
        id: coin.id || index,
        coinName: `${coin.coinName}USDT`,
        favoriteFlag: coin.favoriteFlag,
        isCall: coin.isCall,
        monitoringFlag: coin.monitoringFlag,
        type: coin.type,
        coinNameCmc: coin.coinNameCmc,
        volmc: coin.volmc
      }));

      const allCoins = this.coins
        .map((coin) => ({ ...coin, isSelected: coin.isCall === "1" }))
        .sort((a, b) => b.isSelected - a.isSelected);

      if (fromFilter) {
        this.singleCoinSearchActive = false;
        this.singleSearchCoin = null;
        // Kết quả Block Filter Search: dùng toàn bộ coin trả về để vẽ chart
        const filterCoins = allCoins.slice(0, 20).map((coin) => ({
          coinName: coin.coinName,
          monitoringFlag: coin.monitoringFlag,
          type: coin.type
        }));
        this.coinNames = [
          ...filterCoins,
          ...Array(Math.max(0, 20 - filterCoins.length)).fill(""),
        ];
      } else {
        this.coinNames = allCoins
          .slice(0, 20)
          .map((coin) => {
            if (coin.isSelected) {
              return {
                coinName: coin.coinName,
                monitoringFlag: coin.monitoringFlag,
                type: coin.type
              };
            }
            return "";
          });

        this.coinNames = [
          ...this.coinNames,
          ...Array(20 - this.coinNames.length).fill(""),
        ];
      }

      this.displayedCoins = allCoins.slice(0, 20);
    },

    async fetchCoins() {
      try {
        const data = await BinanceService.getCoins();
        this.processCoinsData(data);
      } catch (error) {
        console.error("Error fetching coins:", error);
      }
    },

    async fetchCoinMetrics(silent = false) {
      if (!silent) {
        this.isCoinMetricsLoading = true;
        this.coinMetricsData = {};
      }
      try {
        // Lấy danh sách coin name từ coinNames (bỏ qua các item rỗng)
        const coinList = this.coinNames
          .filter((item) => item != null && item !== "")
          .map((coin) => {
            // Nếu là object, lấy coinName; nếu là string, dùng trực tiếp
            const coinName = typeof coin === "string" ? coin : coin.coinName;
            // Bỏ "USDT" suffix để gửi lên API
            return coinName.replace("USDT", "");
          });

        if (coinList.length === 0) {
          // Nếu đang ở Single Coin mode thì auto-refresh singleCoinMetrics thay vì bỏ qua
          if (silent && this.singleCoinSearchActive && this.singleSearchCoin) {
            await this.fetchSingleCoinMetrics(this.singleSearchCoin);
          } else {
            console.warn("No coins to fetch metrics for");
          }
          return;
        }

        // Call API binance/get-mkc-fdm
        const data = await BinanceService.getMkcFdm(coinList);

        // Lưu dữ liệu vào coinMetricsData với key là coinName
        data.forEach((item) => {
          let cmcUrl = item.cmcUrl;
          if (item.coinName === "1000CAT" && (!cmcUrl || cmcUrl.includes("caterpillar"))) {
            cmcUrl = "https://coinmarketcap.com/currencies/simonscat/";
          }
          this.coinMetricsData[item.coinName] = {
            marketcap: item.marketcap,
            fdv: item.fdv,
            volMktCapPercent: item.volMktCapPercent,
            oiMktCapPercent: item.oiMktCapPercent,
            cmcUrl: cmcUrl,
            xLink: item.xLink,
          };
        });

        console.log("Coin metrics fetched:", this.coinMetricsData);
      } catch (error) {
        console.error("Error fetching coin metrics:", error?.message || error);
      } finally {
        if (!silent) {
          this.isCoinMetricsLoading = false;
        }
      }
    },

    async handleSearch(period) {
      this.periods = period

      // If currently in single coin search mode, refresh that coin with new period
      if (this.singleCoinSearchActive && this.singleSearchCoin) {
        const coin = { coinName: this.singleSearchCoin };
        await this.handleSingleCoinSearch({ coin, period });
        return;
      }

      // If in Search Coin tab, handle differently
      if (this.currentTab === 'search') {
        // If a coin is selected in Search Coin tab, re-search with new period
        if (this.singleCoinSearchActive && this.singleSearchCoin) {
          const coin = { coinName: this.singleSearchCoin };
          await this.handleSingleCoinSearch({ coin, period });
        }
        return;
      }

      // Normal flow for Favorites and All Coins tabs
      if (this.periods == "") {
        this.$toast.open({
          message: 'Vui lòng chọn thời gian!',
          type: 'error',
          position: 'top-right',
        });
        return false
      }
      const loading = this.$loading.show();

      // Cập nhật danh sách coin từ API nếu chưa có
      if (this.coins.length === 0) {
        await this.fetchCoins();
      }

      // Lấy dữ liệu marketcap và fdv
      await this.fetchCoinMetrics();

      const filteredArray = this.coinNames
        .filter((item) => item != null && item !== "");
      this.coinNames = filteredArray;

      for (let i = 0; i < filteredArray.length; i++) {
        this.loading = true;
        const jobs = [];
        // Khi chọn time frame, luôn gen tất cả chart (bỏ qua kiểm tra metrics)
        const coinName = typeof this.coinNames[i] === "string" ? this.coinNames[i] : this.coinNames[i].coinName;
        jobs.push(this.fetchNotionalData(coinName, i, this.periods));
        jobs.push(
          this.fetchSecondApiDataForCoin(coinName, i, this.periods),
          this.fetchThirdApiDataForCoin(coinName, i, this.periods),
          this.fetchFourApiDataForCoin(coinName, i, this.periods)
        );
        jobs.push(this.fetchFiveApiDataForCoin(coinName, i));
        jobs.push(this.fetchSixApiDataForCoin(coinName, i, this.periods));
        jobs.push(this.fetchFundingRateDataForCoin(coinName, i));
        await Promise.all(jobs);
      }
      const allMetrics = new Set(['openInterest', 'longShort', 'inflow30d', 'inflow24h', 'fundingRate']);
      const validIndices = [];

      for (let i = 0; i < filteredArray.length; i++) {
        if (this.hasAnyChartData(i, allMetrics)) {
          validIndices.push(i);
        }
      }

      // Nếu không có coin nào có data, không hiển thị chart nào
      if (validIndices.length === 0) {
        this.totalChart = 0;
        this.arrayCoin = [];
        this.coinNames = [];
        this.loading = false;
        loading.hide();
        return;
      }

      // Rebuild arrays chỉ với các coin có data
      const newCoinNames = validIndices.map(i => this.coinNames[i]);
      const newChartDataSets = validIndices.map(i => this.chartDataSets[i] || []);
      const newSecondChartDataSets = validIndices.map(i => this.secondChartDataSets[i] || []);
      const newThirdChartDataSets = validIndices.map(i => this.thirdChartDataSets[i] || []);
      const newFourChartDataSets = validIndices.map(i => this.fourChartDataSets[i] || []);
      const newFiveChartDataSets = validIndices.map(i => this.fiveChartDataSets[i] || []);
      const newSixChartDataSets = validIndices.map(i => this.sixChartDataSets[i] || []);
      const newSevenChartDataSets = validIndices.map(i => this.sevenChartDataSets[i] || []);
      const newEightChartDataSets = validIndices.map(i => this.eightChartDataSets[i] || []);



      // Gán lại các arrays
      this.coinNames = newCoinNames;
      this.chartDataSets = newChartDataSets;
      this.secondChartDataSets = newSecondChartDataSets;
      this.thirdChartDataSets = newThirdChartDataSets;
      this.fourChartDataSets = newFourChartDataSets;
      this.fiveChartDataSets = newFiveChartDataSets;
      this.sixChartDataSets = newSixChartDataSets;
      this.sevenChartDataSets = newSevenChartDataSets;
      this.eightChartDataSets = newEightChartDataSets;

      this.totalChart = validIndices.length;
      this.loading = false;
      this.arrayCoin = this.coinNames;
      this.createCharts();
      loading.hide();
    },

    handleTabChange(tab) {
      this.currentTab = tab;
      console.log('Tab changed to:', tab);
    },

    hasAnyChartData(index, selectedMetricsSet) {
      if (!selectedMetricsSet || selectedMetricsSet.size === 0) {
        return false;
      }

      if (selectedMetricsSet.has('openInterest')) {
        const data = this.chartDataSets[index];
        if (data && Array.isArray(data) && data.length > 0) {
          return true;
        }
      }

      if (selectedMetricsSet.has('longShort')) {
        const second = this.secondChartDataSets[index];
        const third = this.thirdChartDataSets[index];
        const four = this.fourChartDataSets[index];
        if (
          (second && Array.isArray(second) && second.length > 0) ||
          (third && Array.isArray(third) && third.length > 0) ||
          (four && Array.isArray(four) && four.length > 0)
        ) {
          return true;
        }
      }

      if (selectedMetricsSet.has('inflow30d')) {
        const five = this.fiveChartDataSets[index];
        if (five && Array.isArray(five) && five.length > 0) {
          return true;
        }
      }

      if (selectedMetricsSet.has('inflow24h')) {
        const six = this.sixChartDataSets[index];
        if (six && Array.isArray(six) && six.length > 0) {
          return true;
        }
      }

      if (selectedMetricsSet.has('fundingRate')) {
        const seven = this.sevenChartDataSets[index];
        if (seven && Array.isArray(seven) && seven.length > 0) {
          return true;
        }
      }

      if (selectedMetricsSet.has('oiMarketcapRatio')) {
        const eight = this.eightChartDataSets[index];
        if (eight && Array.isArray(eight) && eight.length > 0) {
          return true;
        }
      }
      return false;
    },

    async handleRefresh(period) {
      console.log('🔄 handleRefresh called with:', period, 'singleCoinSearchActive:', this.singleCoinSearchActive);

      // Support two payloads emitted from child:
      // - string period => refresh favorites (show charts for favorites)
      // - object { coin, period } => refresh single coin only
      if (period && typeof period === 'object' && period.coin) {
        console.log('🎯 Single-coin refresh requested for:', period.coin.coinName);
        // Single-coin refresh requested (Refresh Coin)
        const payload = { coin: period.coin, period: period.period || this.periods };
        // Ensure we are in single coin mode and refresh that coin
        await this.handleSingleCoinSearch(payload);
        return;
      }

      console.log('⭐ Favorites refresh requested, exiting single coin mode');
      // At this point `period` should be a string (period for favorites)
      this.periods = period;

      // Normal flow for Favorites and All Coins tabs
      if (this.periods == "") {
        this.$toast.open({
          message: 'Vui lòng chọn thời gian!',
          type: 'error',
          position: 'top-right',
        });
        return false
      }
      const loading = this.$loading.show();

      // Ensure we exit single-coin search mode so favorites charts are shown
      this.singleCoinSearchActive = false;

      // Cập nhật danh sách coin từ API nếu chưa có
      if (this.coins.length === 0) {
        await this.fetchCoins();
      }

      // Lấy dữ liệu marketcap và fdv
      await this.fetchCoinMetrics();

      const filteredArray = this.coinNames
        .filter((item) => item != null && item !== "");
      this.coinNames = filteredArray;

      // Sử dụng effectiveSelection để lấy metrics thực tế cần xử lý (không set vào state)
      const sel = new Set(this.effectiveSelection);
      for (let i = 0; i < filteredArray.length; i++) {
        this.loading = true;
        const jobs = [];
        // Lấy coinName từ object nếu là object, nếu không thì dùng trực tiếp
        const coinName = typeof this.coinNames[i] === "string" ? this.coinNames[i] : this.coinNames[i].coinName;
        // Khi refresh, chỉ gen chart cho metrics đã chọn
        if (sel.has('openInterest')) jobs.push(this.fetchNotionalData(coinName, i, this.periods));
        if (sel.has('longShort')) {
          jobs.push(
            this.fetchSecondApiDataForCoin(coinName, i, this.periods),
            this.fetchThirdApiDataForCoin(coinName, i, this.periods),
            this.fetchFourApiDataForCoin(coinName, i, this.periods)
          );
        }
        if (sel.has('inflow30d')) jobs.push(this.fetchFiveApiDataForCoin(coinName, i));
        if (sel.has('inflow24h')) jobs.push(this.fetchSixApiDataForCoin(coinName, i, this.periods));
        if (sel.has('fundingRate')) jobs.push(this.fetchFundingRateDataForCoin(coinName, i));
        await Promise.all(jobs);
      }

      const validIndices = [];

      for (let i = 0; i < filteredArray.length; i++) {
        if (this.hasAnyChartData(i, sel)) {
          validIndices.push(i);
        }
      }

      // Nếu không có coin nào có data, không hiển thị chart nào
      if (validIndices.length === 0) {
        this.totalChart = 0;
        this.arrayCoin = [];
        this.coinNames = [];
        this.loading = false;
        loading.hide();
        return;
      }

      // Rebuild arrays chỉ với các coin có data
      const newCoinNames = validIndices.map(i => this.coinNames[i]);
      const newChartDataSets = validIndices.map(i => this.chartDataSets[i] || []);
      const newSecondChartDataSets = validIndices.map(i => this.secondChartDataSets[i] || []);
      const newThirdChartDataSets = validIndices.map(i => this.thirdChartDataSets[i] || []);
      const newFourChartDataSets = validIndices.map(i => this.fourChartDataSets[i] || []);
      const newFiveChartDataSets = validIndices.map(i => this.fiveChartDataSets[i] || []);
      const newSixChartDataSets = validIndices.map(i => this.sixChartDataSets[i] || []);
      const newSevenChartDataSets = validIndices.map(i => this.sevenChartDataSets[i] || []);
      const newEightChartDataSets = validIndices.map(i => this.eightChartDataSets[i] || []);



      // Gán lại các arrays
      this.coinNames = newCoinNames;
      this.chartDataSets = newChartDataSets;
      this.secondChartDataSets = newSecondChartDataSets;
      this.thirdChartDataSets = newThirdChartDataSets;
      this.fourChartDataSets = newFourChartDataSets;
      this.fiveChartDataSets = newFiveChartDataSets;
      this.sixChartDataSets = newSixChartDataSets;
      this.sevenChartDataSets = newSevenChartDataSets;
      this.eightChartDataSets = newEightChartDataSets;

      this.totalChart = validIndices.length;
      this.loading = false;
      this.arrayCoin = this.coinNames;
      this.createCharts();
      loading.hide();
    },

    // Transform Open Interest data common method
    async getNotionalChartData(coinName, period) {
      const coinNameWithUSDT = coinName.endsWith("USDT") ? coinName : `${coinName}USDT`;

      const raw = await BinanceService.getOpenInterest({
        name: coinNameWithUSDT,
        periodMinutes: period,
      });

      const list =
        Array.isArray(raw) ? raw
          : Array.isArray(raw?.data) ? raw.data
            : Array.isArray(raw?.data?.list) ? raw.data.list
              : [];

      if (!Array.isArray(list) || list.length === 0) {
        return [];
      }

      const sorted = [...list].sort((a, b) => Number(a.timestamp) - Number(b.timestamp));
      const last = sorted.slice(-30);

      return last.map((item) => ({
        x: Number(item.timestamp),
        oi: Number(item.sumOpenInterest),
        notional: Number(item.sumOpenInterestValue),
      }));
    },

    // Fetch Opent Interest with index
    async fetchNotionalData(coinName, index, period) {
      try {
        const chartData = await this.getNotionalChartData(coinName, period);
        this.chartDataSets[index] = chartData;

        if (chartData && chartData.length > 0) {
          const lastPoint = chartData[chartData.length - 1];
          if (lastPoint && lastPoint.notional !== undefined) {
            const newVal = lastPoint.notional;

            const normalizeSymbol = (sym) => {
              if (!sym) return '';
              const firstPart = String(sym).trim().split(' ')[0];
              return firstPart.toUpperCase().replace("USDT", "");
            };

            const targetClean = normalizeSymbol(coinName);
            const parentCoin = this.coins.find(c => normalizeSymbol(c.coinName) === targetClean);
            if (parentCoin) {
              parentCoin.openInterest = newVal;
            }

            if (this.$refs.favoriteBinance) {
              const childCoin = this.$refs.favoriteBinance.coins.find(c => normalizeSymbol(c.coinName) === targetClean);
              if (childCoin) {
                childCoin.openInterest = newVal;
              }
              const childFilteredCoin = this.$refs.favoriteBinance.filteredCoins.find(c => normalizeSymbol(c.coinName) === targetClean);
              if (childFilteredCoin) {
                childFilteredCoin.openInterest = newVal;
              }
              this.$refs.favoriteBinance.$forceUpdate();
            }
          }
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API mới cho ${coinName}:`, error?.message || error);
        this.chartDataSets[index] = [];
      }
    },

    async getLongShortChartData(apiMethod, coinName, period) {
      const coinNameWithUSDT = coinName.endsWith("USDT")
        ? coinName
        : `${coinName}USDT`;

      const data = await apiMethod({
        symbol: coinNameWithUSDT,
        period,
        limit: 30,
      });

      if (!Array.isArray(data)) {
        return [];
      }

      return data
        .map((item) => {
          if (!item.timeGet) return null;

          return {
            x: item.timeGet,
            y: parseFloat(parseFloat(item.longShortRatio || 0).toFixed(3)),
            longPercent: parseFloat(item.longPercent),
            shortPercent: parseFloat(item.shortPercent),
            longShortRatio: parseFloat(parseFloat(item.longShortRatio || 0).toFixed(3)),
          };
        })
        .filter(Boolean)
        .sort((a, b) => a.x - b.x);
    },

    syncRatioToChild(coinName, metricKey, value) {
      if (value === undefined || value === null) return;
      const formattedVal = typeof value === 'number' ? value.toFixed(3) : Number(value).toFixed(3);
      
      const normalizeSymbol = (sym) => {
        if (!sym) return '';
        const firstPart = String(sym).trim().split(' ')[0];
        return firstPart.toUpperCase().replace("USDT", "");
      };
      
      const targetClean = normalizeSymbol(coinName);
      
      const parentCoin = this.coins.find(c => normalizeSymbol(c.coinName) === targetClean);
      if (parentCoin) {
        parentCoin[metricKey] = formattedVal;
      }
      
      if (this.$refs.favoriteBinance) {
        const childCoin = this.$refs.favoriteBinance.coins.find(c => normalizeSymbol(c.coinName) === targetClean);
        if (childCoin) {
          childCoin[metricKey] = formattedVal;
        }
        const childFilteredCoin = this.$refs.favoriteBinance.filteredCoins.find(c => normalizeSymbol(c.coinName) === targetClean);
        if (childFilteredCoin) {
          childFilteredCoin[metricKey] = formattedVal;
        }
        this.$refs.favoriteBinance.$forceUpdate();
      }
    },

    async fetchSecondApiDataForCoin(coinName, index, period) {
      try {
        const chartData = await this.getLongShortChartData(
          (payload) => BinanceService.getLongshortAcc(payload),
          coinName,
          period
        );
        this.secondChartDataSets[index] = chartData;
        if (chartData && chartData.length > 0) {
          this.syncRatioToChild(coinName, 'topAccounts', chartData[chartData.length - 1].longShortRatio);
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API mới cho ${coinName}:`, error.message || error);
        this.secondChartDataSets[index] = [];
      }
    },

    async fetchThirdApiDataForCoin(coinName, index, period) {
      try {
        const chartData = await this.getLongShortChartData(
          (payload) => BinanceService.getLongshortPoss(payload),
          coinName,
          period
        );
        this.thirdChartDataSets[index] = chartData;
        if (chartData && chartData.length > 0) {
          this.syncRatioToChild(coinName, 'topPositions', chartData[chartData.length - 1].longShortRatio);
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API mới cho ${coinName}:`, error.message || error);
        this.thirdChartDataSets[index] = [];
      }

    },
    async fetchFourApiDataForCoin(coinName, index, period) {
      try {
        const chartData = await this.getLongShortChartData(
          (payload) => BinanceService.getLongshort(payload),
          coinName,
          period
        );
        this.fourChartDataSets[index] = chartData;
        if (chartData && chartData.length > 0) {
          this.syncRatioToChild(coinName, 'globalAccounts', chartData[chartData.length - 1].longShortRatio);
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API mới cho ${coinName}:`, error.message || error);
        this.fourChartDataSets[index] = [];
      }
    },

    async getDayLargeInflowChartData(coinName) {
      if (!this.hasSpotMarket(coinName)) {
        return {
          dataSet: [],
          totalInFlow: 0,
          totalInFlowUsdt: 0,
          dates: [],
        };
      }

      const coinNameWithUSDT = coinName.endsWith("USDT")
        ? coinName
        : `${coinName}USDT`;

      const requestBody = {
        symbol: this.normalizeCoinName(coinNameWithUSDT),
        groupCount: "5",
        groupSize: "24",
        period: "HOUR_1",
        volumeType: "BIG",
        targetDays: this.inflowDays,
      };

      const raw = await BinanceService.getDayLargeInflow(requestBody);

      const data = raw?.data ?? raw;
      const list = Array.isArray(data?.netCapitalInList)
        ? data.netCapitalInList
        : [];

      // Lọc bỏ các bản ghi cũ hơn 30 ngày (phòng ngừa coin delist trả về data lịch sử)
      const maxDayAgeMs = 30 * 24 * 60 * 60 * 1000;
      const now = Date.now();
      const recentList = list.filter(it => it.startTime && (now - Number(it.startTime)) < maxDayAgeMs);

      if (recentList.length === 0) {
        return {
          dataSet: [],
          totalInFlow: 0,
          totalInFlowUsdt: 0,
          dates: [],
        };
      }

      // De-duplicate items by date (newest/first duplicate is kept because list is newest-first)
      const seenDates = new Set();
      const uniqueList = [];
      for (const it of recentList) {
        const d = new Date(it.startTime || Date.now());
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const dateStr = `${day}/${month}`;
        if (!seenDates.has(dateStr)) {
          seenDates.add(dateStr);
          uniqueList.push(it);
        }
      }

      let series = uniqueList
        .map((it) =>
          Number.parseFloat(it?.netCapitalInflowVolume ?? it?.value ?? 0)
        )
        .filter(Number.isFinite);

      const dates = uniqueList.map((it) => {
        const d = new Date(it.startTime || Date.now());
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        return `${day}/${month}`;
      });

      let finalDates = dates;
      if (series.length > this.inflowDays) {
        series = series.slice(0, this.inflowDays);
        finalDates = finalDates.slice(0, this.inflowDays);
      } else if (series.length < this.inflowDays) {
        const missingCount = this.inflowDays - series.length;
        
        // Pad series with 0 at the END (since list is newest-first, oldest is at the end)
        series = series.concat(Array(missingCount).fill(0));
        
        // Pad finalDates with successive older days at the END (oldest values)
        const paddedDates = [];
        const oldestTime = uniqueList.length > 0 ? (uniqueList[uniqueList.length - 1].startTime || Date.now()) : Date.now();
        for (let i = 1; i <= missingCount; i++) {
          const d = new Date(oldestTime - i * 86400000);
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          paddedDates.push(`${day}/${month}`);
        }
        finalDates = finalDates.concat(paddedDates);
      }

      const totalInFlowNum = Number.parseFloat(data?.totalInFlow);
      const totalInFlow = Number.isFinite(totalInFlowNum)
        ? totalInFlowNum
        : series.reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);

      const totalInFlowUsdtNum = Number.parseFloat(data?.totalInFlowUsdt);
      const totalInFlowUsdt = Number.isFinite(totalInFlowUsdtNum)
        ? totalInFlowUsdtNum
        : 0;

      return {
        dataSet: series,
        totalInFlow,
        totalInFlowUsdt,
        dates: finalDates,
      };
    },
    async fetchFiveApiDataForCoin(coinName, index) {
      try {
        if (!Array.isArray(this.fiveChartDataSets)) {
          this.fiveChartDataSets = [];
        }

        this.fiveChartDataSets[index] =
          await this.getDayLargeInflowChartData(coinName);
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API thứ ba cho ${coinName}:`, error?.message || error);

        if (!Array.isArray(this.fiveChartDataSets)) {
          this.fiveChartDataSets = [];
        }

        this.fiveChartDataSets[index] = {
          dataSet: Array(this.inflowDays).fill(0),
          totalInFlow: 0,
          totalInFlowUsdt: 0,
        };
      }
    },

    async get24HourLargeInflowChartData(coinName, period) {
      if (!this.hasSpotMarket(coinName)) {
        return [];
      }

      const coinNameWithUSDT = coinName.endsWith("USDT")
        ? coinName
        : `${coinName}USDT`;

      const requestBody = {
        symbol: this.normalizeCoinName(coinNameWithUSDT),
        period: period,
        size: "288",
      };

      const raw = await BinanceService.get24HourLargeInflow(requestBody);

      const dataArray = raw?.data ?? raw ?? [];

      if (!Array.isArray(dataArray) || dataArray.length === 0) {
        return [];
      }

      const maxAgeMs = 48 * 60 * 60 * 1000;
      const now = Date.now();

      return dataArray
        .filter((item) => item.startTime && (now - Number(item.startTime)) < maxAgeMs)
        .map((item) => {
          return {
            x: Number(item.startTime),
            y: parseFloat(Number(item.netCapitalInflowVolume).toFixed(2)),
          };
        });
    },

    async fetchSixApiDataForCoin(coinName, index, period) {
      try {
        this.sixChartDataSets[index] =
          await this.get24HourLargeInflowChartData(coinName, period);
      } catch (error) {
        this.sixChartDataSets[index] = [];
        console.error(
          `Lỗi khi tải dữ liệu từ API thứ ba cho ${coinName}:`,
          error.message || error
        );
      }
    },

    async getFundingRateChartData(coinName, period) {
      const coinNameWithUSDT = coinName.endsWith("USDT")
        ? coinName
        : `${coinName}USDT`;

      const requestBody = {
        symbol: coinNameWithUSDT,
        period,
      };

      const res = await BinanceService.getFundingRate(requestBody);
      const raw = filterZeroData(res);
      const list = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.data)
          ? raw.data
          : [];

      const fundingRateResult = list
        .map((it) => {
          const t = new Date(it.syncTime).getTime();
          const y = parseFloat(it.fundingRate);

          if (!Number.isFinite(t) || !Number.isFinite(y)) {
            return null;
          }

          return { x: t, y };
        })
        .filter(Boolean)
        .sort((a, b) => a.x - b.x);

      const ratioResult = list
        .map((it) => {
          const t = new Date(it.syncTime).getTime();
          const y = parseFloat(it.ratio);

          if (!Number.isFinite(t) || !Number.isFinite(y)) {
            return null;
          }

          return { x: t, y };
        })
        .filter(Boolean)
        .sort((a, b) => a.x - b.x);
      return {
        fundingRateResult,
        ratioResult,
      };
    },

    async fetchFundingRateDataForCoin(coinName, index) {
      try {
        const { fundingRateResult, ratioResult } =
          await this.getFundingRateChartData(coinName, this.periods);

        this.sevenChartDataSets[index] = fundingRateResult;
        this.eightChartDataSets[index] = ratioResult;
      } catch (error) {
        this.sevenChartDataSets[index] = [];
        this.eightChartDataSets[index] = [];

        console.error(
          `Lỗi khi tải dữ liệu Funding Rate cho ${coinName}:`,
          error.message || error
        );
      }
    },



    createCharts() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          const kill = c => { try { c && !c._destroyed && c.destroy(); } catch (_) { /* ignore */ } };
          [this.charts, this.secondCharts, this.thirdCharts, this.fourCharts, this.fiveCharts, this.sixCharts, this.sevenCharts, this.eightCharts]
            .forEach(arr => {
              (arr || []).forEach((c, i) => {
                const cv = c?.canvas;
                if (!cv || !document.body.contains(cv)) { kill(c); arr[i] = null; }
              });
            });
          let periods = this.periods;
          this.chartIds.slice(0, this.totalChart).forEach((chartId, index) => {
            const canvas = document.getElementById(chartId);
            if (canvas) {
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (this.charts[index]) {
                try { this.charts[index].destroy(); } catch (e) {
                  // skip
                }
                this.charts[index] = null;
              }
              const dataSet = this.chartDataSets[index] || [];
              const labels = dataSet.map(item => item.x);
              const oi = dataSet.map(item => item.oi);
              const notional = dataSet.map(item => item.notional);

              const yL = niceMinMax(oi, 0.06);
              const yR = niceMinMax(notional, 0.06);
              const LEFT_TICKS = 4;
              const RIGHT_TICKS = 4;
              const leftStep = (yL.max - yL.min) / Math.max(1, LEFT_TICKS - 1);
              const rightStep = (yR.max - yR.min) / Math.max(1, RIGHT_TICKS - 1);

              this.charts[index] = new ChartJS(ctx, {
                type: "bar",
                data: {
                  labels,
                  datasets: [
                    {
                      type: 'bar',
                      label: `Open Interest (${this.getCoinName(this.coinNames[index]).replace('USDT', '')})`,
                      data: oi,
                      yAxisID: 'y',
                      backgroundColor: '#f0b90b',
                      borderColor: '#dca600',
                      borderSkipped: false,
                      borderWidth: 0,
                      borderRadius: 2,
                      barPercentage: 0.6,
                      categoryPercentage: 0.8,
                      maxBarThickness: 12,
                      datalabels: { display: false },
                      order: 1
                    },
                    {
                      type: 'line',
                      label: 'Notional Value of Open Interest (USDT)',
                      data: notional,
                      yAxisID: 'y1',
                      borderColor: '#ffffff',
                      borderWidth: 2,
                      order: 999,
                      z: 10,
                      showLine: true,
                      spanGaps: true,
                      pointRadius: 2,
                      pointHoverRadius: 3,
                      pointBackgroundColor: '#0b1220',
                      pointBorderColor: '#e5e7eb',
                      pointBorderWidth: 1,
                      tension: 0.25,
                      cubicInterpolationMode: 'monotone',
                      fill: false,
                      datalabels: { display: false }
                    }
                  ],
                },
                options: {
                  responsive: true,
                  maintainAspectRatio: false,
                  layout: { padding: 0 },
                  interaction: {
                    mode: 'index',
                    intersect: false
                  },
                  plugins: {
                    legend: {
                      display: true,
                      reverse: false
                    },
                    datalabels: {
                      display: false,
                    },
                    title: {
                      display: true,
                      text: `Open Interest(${this.getCoinName(this.coinNames[index])})`,
                    },
                    tooltip: {
                      backgroundColor: '#111827',
                      borderColor: '#374151',
                      borderWidth: 1,
                      titleColor: '#e5e7eb',
                      bodyColor: '#e5e7eb',
                      callbacks: {
                        title(items) {
                          const ts = items[0]?.parsed.x;
                          return ts ? new Date(ts).toLocaleTimeString() : '';
                        },
                        label(c) {
                          const name = c.dataset.label;
                          const value = c.parsed.y;
                          if (name.includes('Notional') || name.includes('Value')) {
                            return ` ${name}: ${formatNumber(value)}`;
                          }
                          return ` ${name}: ${value % 1 === 0 ? fmtComma(value) : value.toFixed(2)}`;
                        }
                      }
                    },
                  },
                  scales: {
                    x: {
                      type: 'time',
                      time: {
                        unit: this.periods === "1h" || this.periods === "4h" ? "hour"
                          : this.periods === "1d" ? "day" : "minute"
                      },
                      grid: {
                        color: 'rgba(255,255,255,0.10)',
                        drawTicks: true,
                        drawOnChartArea: true
                      },
                      offset: false,
                      bounds: 'data',
                      ticks: {
                        color: '#9ca3af',
                        maxTicksLimit: 15,
                        count: 15,
                        autoSkip: true,
                        autoSkipPadding: 8,

                        maxRotation: 55,
                        minRotation: 35,
                        padding: 2,

                        callback: function (value) {
                          const date = new Date(value);
                          if (periods === "1d") {
                            return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
                          } else {
                            return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                          }
                        },
                      },
                    },
                    y: {
                      position: 'left',
                      min: yL.min,
                      max: yL.max,
                      beginAtZero: false,
                      grid: { color: 'rgba(255,255,255,0.10)', drawOnChartArea: true, drawTicks: true },
                      ticks: {
                        color: '#9ca3af', font: { size: 11 }, padding: 0, count: LEFT_TICKS, stepSize: leftStep,
                        callback: function (v) {
                          const a = Math.abs(v);
                          if (a >= 1e12) return (v / 1e12).toFixed(2) + 'T';
                          if (a >= 1e9) return (v / 1e9).toFixed(2) + 'B';
                          if (a >= 1e6) return (v / 1e6).toFixed(2) + 'M';
                          if (a >= 1e3) return (v / 1e3).toFixed(2) + 'K';
                          return v % 1 === 0 ? v : v.toFixed(2);
                        }
                      },
                      title: { display: false }
                    },
                    y1: {
                      position: 'right',
                      min: yR.min,
                      max: yR.max,
                      beginAtZero: false,
                      grid: { display: false, drawOnChartArea: false },
                      ticks: {
                        color: '#9ca3af', font: { size: 11 }, padding: 0, count: RIGHT_TICKS, stepSize: rightStep,
                        callback: function (v) {
                          const a = Math.abs(v);
                          if (a >= 1e12) return (v / 1e12).toFixed(2) + 'T';
                          if (a >= 1e9) return (v / 1e9).toFixed(2) + 'B';
                          if (a >= 1e6) return (v / 1e6).toFixed(2) + 'M';
                          if (a >= 1e3) return (v / 1e3).toFixed(2) + 'K';
                          return v % 1 === 0 ? v : v.toFixed(2);
                        }
                      },
                      title: { display: false }
                    }
                  }
                },
                plugins: [stripeBg]
              });
              if (canvas && canvas.parentNode) {
                canvas.parentNode.style.height = '300px';
              }
            }
          });

          this.secondChartIds
            .slice(0, this.totalChart)
            .forEach((chartId, index) => {
              const canvas = document.getElementById(chartId);
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (canvas) {
                if (this.secondCharts[index]) {
                  try { this.secondCharts[index].destroy(); } catch (e) {
                    // skip
                  }
                  this.secondCharts[index] = null;
                }

                const dataSet = this.secondChartDataSets[index] || [];
                const minValue = Math.min(
                  ...dataSet.map((item) => item.longShortRatio)
                );
                const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                const startValue =
                  minValue - roundedValue <= 0.05
                    ? roundedValue
                    : roundedValue + 0.05;

                this.secondCharts[index] = new ChartJS(ctx, {
                  type: "bar",
                  data: {
                    datasets: [
                      {
                        label: "Tỷ lệ Long/Short (Tài khoản)",
                        type: "line",
                        data: dataSet,
                        borderColor: "rgba(0, 0, 0, 1)",
                        pointBackgroundColor: "rgba(255, 255, 255, 1)",
                        pointBorderColor: "rgba(255, 255, 255, 0.7)",
                        pointBorderWidth: 0.2,
                        pointRadius: 2,
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        yAxisID: "y-line",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Tài khoản Long",
                        data: dataSet.map((item) => item.longPercent),
                        backgroundColor: "rgba(0, 209, 112, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Tài khoản Short",
                        data: dataSet.map((item) => item.shortPercent),
                        backgroundColor: "rgba(231, 76, 60, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                    ],
                  },
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                      },
                      datalabels: {
                        display: false,
                      },
                      title: {
                        display: true,
                        text: `Top Accounts(${this.getCoinName(this.coinNames[index])})`,
                      },
                      tooltip: {
                        callbacks: {
                          label: function (tooltipItem) {
                            const dataset = tooltipItem.dataset;
                            const rawData = dataset.meta.customValue;
                            const index = tooltipItem.dataIndex;
                            const shortPercent = rawData[index].shortPercent || 0;
                            const longPercent = rawData[index].longPercent || 0;
                            const longShortRatio =
                              rawData[index].longShortRatio || 0;
                            return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        type: "time",
                        time: {
                          ...(this.periods === "1d" && {
                            round: "day",
                          }),
                          unit:
                            this.periods === "1h" || this.periods === "4h"
                              ? "hour"
                              : this.periods === "1d"
                                ? "day"
                                : "minute",
                        },
                        labels: dataSet.map((item) => item.x),
                        ticks: {
                          maxTicksLimit: 20,
                          ...(this.periods !== "1d" && {
                            stepSize:
                              this.periods === "1h"
                                ? 1
                                : this.periods === "4h"
                                  ? 4
                                  : 5,
                          }),
                          callback: function (value) {
                            const date = new Date(value);
                            if (periods === "1d") {
                              return date.toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                              });
                            } else {
                              return date.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }
                          },
                        },
                        stacked: true,
                      },
                      "y-line": {
                        id: "y-line",
                        position: "right",
                        grid: {
                          drawOnChartArea: false,
                        },
                        beginAtZero: false,
                        min: startValue,
                        ticks: {
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },
                      y: {
                        display: false,
                        grid: {
                          display: false,
                        },
                      },
                    },
                  },
                  plugins: [stripeBg]
                });
              }
            });

          this.thirdChartIds
            .slice(0, this.totalChart)
            .forEach((chartId, index) => {
              const canvas = document.getElementById(chartId);
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (canvas) {
                if (this.thirdCharts[index]) {
                  try { this.thirdCharts[index].destroy(); } catch (e) {
                    // skip
                  }
                  this.thirdCharts[index] = null;
                }

                const dataSet = this.thirdChartDataSets[index] || [];
                const minValue = Math.min(
                  ...dataSet.map((item) => item.longShortRatio)
                );
                const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                const startValue =
                  minValue - roundedValue <= 0.05
                    ? roundedValue
                    : roundedValue + 0.05;

                this.thirdCharts[index] = new ChartJS(ctx, {
                  type: "bar",
                  data: {
                    datasets: [
                      {
                        label: "Tỷ lệ Long/Short (Vị thế)",
                        type: "line",
                        data: dataSet,
                        borderColor: "rgba(0, 0, 0, 1)",
                        pointBackgroundColor: "rgba(255, 255, 255, 1)",
                        pointBorderColor: "rgba(255, 255, 255, 0.7)",
                        pointBorderWidth: 0.2,
                        pointRadius: 2,
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        yAxisID: "y-line",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Vị thế Long",
                        data: dataSet.map((item) => item.longPercent),
                        backgroundColor: "rgba(0, 209, 112, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Vị thế Short",
                        data: dataSet.map((item) => item.shortPercent),
                        backgroundColor: "rgba(231, 76, 60, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                    ],
                  },
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                      },
                      datalabels: {
                        display: false,
                      },
                      title: {
                        display: true,
                        text: `Top Positions(${this.getCoinName(this.coinNames[index])})`,
                      },
                      tooltip: {
                        callbacks: {
                          label: function (tooltipItem) {
                            const dataset = tooltipItem.dataset;
                            const rawData = dataset.meta.customValue;
                            const index = tooltipItem.dataIndex;
                            const shortPercent = rawData[index].shortPercent || 0;
                            const longPercent = rawData[index].longPercent || 0;
                            const longShortRatio =
                              rawData[index].longShortRatio || 0;
                            return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        type: "time",
                        time: {
                          ...(this.periods === "1d" && {
                            round: "day",
                          }),
                          unit:
                            this.periods === "1h" || this.periods === "4h"
                              ? "hour"
                              : this.periods === "1d"
                                ? "day"
                                : "minute",
                        },
                        labels: dataSet.map((item) => item.x),
                        ticks: {
                          maxTicksLimit: 20,
                          ...(this.periods !== "1d" && {
                            stepSize:
                              this.periods === "1h"
                                ? 1
                                : this.periods === "4h"
                                  ? 4
                                  : this.periods === "15m"
                                    ? 15
                                    : 5,
                          }),
                          callback: function (value) {
                            const date = new Date(value);
                            if (periods === "1d") {
                              return date.toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                              });
                            } else {
                              return date.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }
                          },
                        },
                        stacked: true,
                      },
                      "y-line": {
                        id: "y-line",
                        position: "right",
                        grid: {
                          drawOnChartArea: false,
                        },
                        beginAtZero: false,
                        min: startValue,
                        ticks: {
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },
                      y: {
                        display: false,
                        grid: {
                          display: false,
                        },
                      },
                    },
                  },
                });
              }
            });
          this.fourChartIds
            .slice(0, this.totalChart)
            .forEach((chartId, index) => {
              const canvas = document.getElementById(chartId);
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (canvas) {
                if (this.fourCharts[index]) {
                  try { this.fourCharts[index].destroy(); } catch (e) {
                    // skip
                  }
                  this.fourCharts[index] = null;
                }

                const dataSet = this.fourChartDataSets[index] || [];
                const minValue = Math.min(
                  ...dataSet.map((item) => item.longShortRatio)
                );
                const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                const startValue =
                  minValue - roundedValue <= 0.05
                    ? roundedValue
                    : roundedValue + 0.05;
                this.fourCharts[index] = new ChartJS(ctx, {
                  type: "bar",
                  data: {
                    datasets: [
                      {
                        label: "Tỷ lệ Long/Short",
                        type: "line",
                        data: dataSet,
                        borderColor: "rgba(0, 0, 0, 1)",
                        pointBackgroundColor: "rgba(255, 255, 255, 1)",
                        pointBorderColor: "rgba(255, 255, 255, 0.7)",
                        pointBorderWidth: 0.2,
                        pointRadius: 2,
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        yAxisID: "y-line",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Tài khoản Long",
                        data: dataSet.map((item) => item.longPercent),
                        backgroundColor: "rgba(0, 209, 112, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                      {
                        label: "% Tài khoản Short",
                        data: dataSet.map((item) => item.shortPercent),
                        backgroundColor: "rgba(231, 76, 60, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: {
                          customValue: dataSet,
                        },
                      },
                    ],
                  },
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,

                    plugins: {
                      datalabels: {
                        display: false,
                      },

                      legend: {
                        display: true,
                      },
                      title: {
                        display: true,
                        text: `Accounts(${this.getCoinName(this.coinNames[index])})`,
                      },
                      tooltip: {
                        callbacks: {
                          label: function (tooltipItem) {
                            const dataset = tooltipItem.dataset;
                            const rawData = dataset.meta.customValue;
                            const index = tooltipItem.dataIndex;
                            const shortPercent = rawData[index].shortPercent || 0;
                            const longPercent = rawData[index].longPercent || 0;
                            const longShortRatio =
                              rawData[index].longShortRatio || 0;
                            return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        type: "time",
                        time: {
                          ...(this.periods === "1d" && {
                            round: "day",
                          }),
                          unit:
                            this.periods === "1h" || this.periods === "4h"
                              ? "hour"
                              : this.periods === "1d"
                                ? "day"
                                : "minute",
                        },
                        labels: dataSet.map((item) => item.x),
                        ticks: {
                          maxTicksLimit: 20,
                          ...(this.periods !== "1d" && {
                            stepSize:
                              this.periods === "1h"
                                ? 1
                                : this.periods === "4h"
                                  ? 4
                                  : this.periods === "15m"
                                    ? 15
                                    : 5,
                          }),
                          callback: function (value) {
                            const date = new Date(value);
                            if (periods === "1d") {
                              return date.toLocaleDateString("en-GB", {
                                day: "2-digit",
                                month: "2-digit",
                              });
                            } else {
                              return date.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                            }
                          },
                        },
                        stacked: true,
                      },

                      "y-line": {
                        position: "right",
                        beginAtZero: false,
                        min: startValue,
                        ticks: {
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },

                      y: {
                        display: false,
                        grid: {
                          display: false,
                        },
                        ticks: {
                          beginAtZero: false,
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },
                    },
                  },
                });
              }
            });
          this.fiveChartIds
            .slice(0, this.totalChart)
            .forEach((chartId, index) => {
              const canvas = document.getElementById(chartId);
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (canvas) {
                if (this.fiveCharts[index]) {
                  try { this.fiveCharts[index].destroy(); } catch (e) {
                    // skip
                  }
                  this.fiveCharts[index] = null;
                }
                const dataGet = this.fiveChartDataSets[index] || [];
                const totalInFlow = Math.abs(dataGet.totalInFlow);
                let dataTotalInFlow = 0;
                if (totalInFlow >= 1000000000) {
                  dataTotalInFlow = (totalInFlow / 1000000000).toFixed(2) + "B";
                } else if (totalInFlow >= 1000000) {
                  dataTotalInFlow = (totalInFlow / 1000000).toFixed(2) + "M";
                } else if (totalInFlow >= 1000) {
                  dataTotalInFlow = (totalInFlow / 1000).toFixed(2) + "K";
                }

                // Format totalInFlowUsdt
                const totalInFlowUsdt = Math.abs(dataGet.totalInFlowUsdt || 0);
                let dataTotalInFlowUsdt = 0;
                if (totalInFlowUsdt >= 1000000000) {
                  dataTotalInFlowUsdt = (totalInFlowUsdt / 1000000000).toFixed(2) + "B";
                } else if (totalInFlowUsdt >= 1000000) {
                  dataTotalInFlowUsdt = (totalInFlowUsdt / 1000000).toFixed(2) + "M";
                } else if (totalInFlowUsdt >= 1000) {
                  dataTotalInFlowUsdt = (totalInFlowUsdt / 1000).toFixed(2) + "K";
                }

                const dataSet = dataGet.dataSet;
                if (!dataSet) return;
                this.fiveCharts[index] = new ChartJS(ctx, {
                  type: "bar",
                  data: {
                    labels: dataGet.dates || Array.from({ length: this.inflowDays }, (_, i) => i + 1),
                    datasets: [
                      {
                        label: `${this.inflowDays}d Trading Flow: ${dataTotalInFlow} || ${dataTotalInFlowUsdt}`,
                        data: dataSet,
                        backgroundColor: dataSet.map((value) =>
                          value < 0
                            ? "rgba(255, 0, 0, 0.6)"
                            : "rgba(0, 128, 0, 0.6)"
                        ),
                        borderColor: dataSet.map((value) =>
                          value < 0 ? "rgb(255, 0, 0)" : "rgb(0, 128, 0)"
                        ),
                        borderWidth: 1,
                      },
                    ],
                  },
                  options: {
                    interaction: {
                      mode: "index",
                      intersect: false,
                    },
                    maintainAspectRatio: false,
                    responsive: true,
                    scales: {
                      x: {
                        display: false,
                        reverse: true
                      },
                      y: {
                        display: true,
                        ticks: {
                          callback: function (value) {
                            const absValue = Math.abs(value);
                            if (absValue >= 1000000000) {
                              return (absValue / 1000000000).toFixed(2) + "B";
                            } else if (absValue >= 1000000) {
                              return (absValue / 1000000).toFixed(2) + "M";
                            } else if (absValue >= 1000) {
                              return (absValue / 1000).toFixed(2) + "K";
                            }
                            return absValue;
                          },
                        },
                      },
                    },
                    plugins: {
                      title: {
                        display: true,
                        text: `${this.inflowDays}d Trading Flow: ${dataTotalInFlow} || ${dataTotalInFlowUsdt}`,
                        font: {
                          style: "italic",
                        },
                        color: dataGet.totalInFlow < 0 ? "red" : "green",
                        padding: 30,
                      },

                      tooltip: {
                        enabled: true,
                        callbacks: {
                          title: function (tooltipItems) {
                            return `Thời gian: ${tooltipItems[0].label}`;
                          },
                          label: function (context) {
                            const value = context.raw;
                            const absValue = Math.abs(value);
                            let formattedValue = value;
                            if (absValue >= 1000000000) {
                              formattedValue = (value / 1000000000).toFixed(2) + "B"; // Tỉ
                            } else if (absValue >= 1000000) {
                              formattedValue = (value / 1000000).toFixed(2) + "M"; // Triệu
                            } else if (absValue >= 1000) {
                              formattedValue = (value / 1000).toFixed(2) + "K"; // Nghìn
                            }
                            return `Giá trị: ${formattedValue}`;
                          }
                        }
                      },
                      legend: {
                        display: false,
                      },
                      datalabels: {
                        display: true,
                        anchor: "end",
                        align: "top",
                        color: function (context) {
                          return context.dataset.data[context.dataIndex] < 0 ? "red" : "green";
                        },
                        font: {
                          weight: "bold",
                          size: 10,
                        },
                        formatter: function (value) {
                          const absValue = Math.abs(value);
                          if (value === 0) return "0";
                          if (absValue >= 1000000000) {
                            return (value / 1000000000).toFixed(2) + "B";
                          } else if (absValue >= 1000000) {
                            return (value / 1000000).toFixed(2) + "M";
                          } else if (absValue >= 1000) {
                            return (value / 1000).toFixed(2) + "K";
                          }
                          return value;
                        },
                      },
                    },
                  },
                });
              }
            });
          this.sixChartIds.slice(0, this.totalChart).forEach((chartId, index) => {
            const canvas = document.getElementById(chartId);
            if (!canvas) return;
            if (canvas.offsetParent === null) return;
            const exist = ChartJS.getChart(canvas);
            if (exist) {
              try { exist.destroy(); } catch (e) {
                // skip
              }
            }
            const ctx = canvas.getContext("2d");
            if (!ctx) return;
            if (canvas) {
              if (this.sixCharts[index]) {
                try { this.sixCharts[index].destroy(); } catch (e) {
                  // skip
                }
                this.sixCharts[index] = null;
              }

              const dataSet = this.sixChartDataSets[index] || [];
              if (!dataSet) return;

              this.sixCharts[index] = new ChartJS(ctx, {
                type: "line",
                data: {
                  datasets: [
                    {
                      data: dataSet,
                      borderColor: "#f39c12",
                      backgroundColor: "rgba(243, 156, 18, 0.5)",
                      fill: true,
                      tension: 0,
                      borderWidth: 2,
                      pointRadius: 0,
                    },
                  ],
                },
                options: {
                  interaction: {
                    mode: "index",
                    intersect: false,
                  },
                  plugins: {
                    datalabels: {
                      display: false,
                    },
                    legend: {
                      display: false,
                    },
                    title: {
                      display: true,
                      text: `Trading Flow(${this.getCoinName(this.coinNames[index])})`,
                    },
                    tooltip: {
                      enabled: true,
                    },
                  },
                  scales: {
                    x: {
                      type: "time",
                      grid: {
                        display: false,
                      },
                      time: {
                        unit: "hour",
                      },
                      labels: dataSet.map((item) => item.x),
                      ticks: {
                        maxTicksLimit: 30,
                        stepSize: 6,
                        callback: function (value) {
                          const date = new Date(value);
                          const time = date.toLocaleTimeString("en-GB", {
                            hour: "2-digit",
                            minute: "2-digit",
                          });
                          return time;
                        },
                      },
                      stacked: true,
                    },
                    y: {
                      display: true,
                    },
                  },
                  responsive: true,
                  maintainAspectRatio: false,
                },
              });
            }
          });
          this.sevenChartIds.slice(0, this.totalChart).forEach((chartId, index) => {
            const canvas = document.getElementById(chartId);
            if (canvas) {
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (this.sevenCharts[index]) {
                try { this.sevenCharts[index].destroy(); } catch (e) {
                  // skip
                }
                this.sevenCharts[index] = null;
              }
              const dataSet = this.sevenChartDataSets[index] || [];
              this.sevenCharts[index] = new ChartJS(ctx, {
                type: "line",
                data: {
                  datasets: [
                    {
                      label: `Funding Rate (%)`,
                      data: dataSet,
                      borderColor: "#e91e63",
                      backgroundColor: "rgba(233, 30, 99, 0.25)",
                      pointBackgroundColor: "rgba(233, 30, 99, 0.5)",
                      pointBorderColor: "rgba(233, 30, 99, 0.2)",
                      pointBorderWidth: 0.2,
                      pointRadius: 2,
                      fill: true,
                      tension: 0,
                      borderWidth: 2,
                    },
                  ],
                },
                options: {
                  plugins: {
                    legend: {
                      display: true,
                    },
                    datalabels: {
                      display: false,
                    },
                    title: {
                      display: true,
                      text: `Funding Rate (${this.getCoinName(this.coinNames[index])})`,
                    },
                    tooltip: {
                      enabled: true,
                      callbacks: {
                        label: function (ctx) {
                          const v = ctx.parsed?.y;
                          if (v == null) return '';
                          return `Funding rate: ${Number(v).toFixed(4)}%`;
                        }
                      }
                    },
                  },
                  interaction: {
                    mode: "index",
                    intersect: false,
                  },
                  scales: {
                    x: {
                      type: "time",
                      time: {
                        ...(this.periods === "1d" && {
                          round: "day",
                        }),
                        unit:
                          this.periods === "1h" || this.periods === "4h"
                            ? "hour"
                            : this.periods === "1d"
                              ? "day"
                              : "minute",
                      },
                      labels: dataSet.map((item) => item.x),
                      ticks: {
                        maxTicksLimit: 20,
                        ...(this.periods !== "1d" && {
                          stepSize:
                            this.periods === "1h"
                              ? 1
                              : this.periods === "4h"
                                ? 4
                                : 5,
                        }),
                        callback: (val) => {
                          const ts = typeof val === 'number' ? val : Date.parse(val);
                          if (!Number.isFinite(ts)) return String(val ?? "");
                          const d = new Date(ts);

                          const tz = 'Asia/Ho_Chi_Minh';
                          if (periods === "1d") {
                            return new Intl.DateTimeFormat('en-GB', {
                              timeZone: tz, day: '2-digit', month: '2-digit'
                            }).format(d);
                          }

                          const isHourUnit = periods === "1h" || periods === "4h" || periods === "8h";
                          return new Intl.DateTimeFormat('en-GB', {
                            timeZone: tz,
                            hour: '2-digit',
                            minute: isHourUnit ? undefined : '2-digit'
                          }).format(d);
                        },
                      },
                      stacked: true,
                    },
                    y: {
                      ticks: {
                        callback: (v) => `${Number(v).toFixed(4)}%`,
                      },
                    },
                  },
                  responsive: true,
                  maintainAspectRatio: false,
                },
              });
            }
          });

          // Create Eight Chart (OI/Marketcap Ratio)
          this.eightChartIds.slice(0, this.totalChart).forEach((chartId, index) => {
            const canvas = document.getElementById(chartId);
            if (canvas) {
              if (!canvas) return;
              if (canvas.offsetParent === null) return;
              const exist = ChartJS.getChart(canvas);
              if (exist) {
                try { exist.destroy(); } catch (e) {
                  // skip
                }
              }
              const ctx = canvas.getContext("2d");
              if (!ctx) return;
              if (this.eightCharts[index]) {
                try { this.eightCharts[index].destroy(); } catch (e) {
                  // skip
                }
                this.eightCharts[index] = null;
              }
              const dataSet = this.eightChartDataSets[index] || [];
              this.eightCharts[index] = new ChartJS(ctx, {
                type: "line",
                data: {
                  datasets: [
                    {
                      label: `OI/Marketcap Ratio`,
                      data: dataSet,
                      borderColor: "#4ade80",
                      backgroundColor: "rgba(74, 222, 128, 0.25)",
                      fill: true,
                      tension: 0,
                      borderWidth: 2,
                    },
                  ],
                },
                options: {
                  plugins: {
                    legend: {
                      display: true,
                    },
                    datalabels: {
                      display: false,
                    },
                    title: {
                      display: true,
                      text: `OI/Marketcap Ratio (${this.getCoinName(this.coinNames[index])})`,
                    },
                    tooltip: {
                      enabled: true,
                      callbacks: {
                        label: function (ctx) {
                          const v = ctx.parsed?.y;
                          if (v == null) return '';
                          return `OI/Marketcap Ratio: ${Number(v).toFixed(2)}`;
                        }
                      }
                    },
                  },
                  interaction: {
                    mode: "index",
                    intersect: false,
                  },
                  scales: {
                    x: {
                      type: "time",
                      time: {
                        ...(this.periods === "1d" && {
                          round: "day",
                        }),
                        unit:
                          this.periods === "1h" || this.periods === "4h"
                            ? "hour"
                            : this.periods === "1d"
                              ? "day"
                              : "minute",
                      },
                      labels: dataSet.map((item) => item.x),
                      ticks: {
                        maxTicksLimit: 20,
                        ...(this.periods !== "1d" && {
                          stepSize:
                            this.periods === "1h"
                              ? 1
                              : this.periods === "4h"
                                ? 4
                                : 5,
                        }),
                        callback: (val) => {
                          const ts = typeof val === 'number' ? val : Date.parse(val);
                          if (!Number.isFinite(ts)) return String(val ?? "");
                          const d = new Date(ts);

                          const tz = 'Asia/Ho_Chi_Minh';
                          if (periods === "1d") {
                            return new Intl.DateTimeFormat('en-GB', {
                              timeZone: tz, day: '2-digit', month: '2-digit'
                            }).format(d);
                          }

                          const isHourUnit = periods === "1h" || periods === "4h" || periods === "8h";
                          return new Intl.DateTimeFormat('en-GB', {
                            timeZone: tz,
                            hour: '2-digit',
                            minute: isHourUnit ? undefined : '2-digit'
                          }).format(d);
                        },
                      },
                      stacked: true,
                    },
                    y: {
                      ticks: {
                        callback: (v) => `${Number(v).toFixed(2)}`,
                      },
                    },
                  },
                  responsive: true,
                  maintainAspectRatio: false,
                },
              });
            }
          });
        });
      });
    },
    async handleMetrics(list) {
      this.selectedMetrics = Array.isArray(list) ? list : [];
      await MetricService.updateMetrics({
        metrics: list
      });
    },
    handleInflowDaysChanged(days) {
      this.inflowDays = days;
      if (this.singleCoinSearchActive && this.singleSearchCoin) {
        this.handleSingleCoinSearch({
          coin: { coinName: this.singleSearchCoin },
          period: this.periods
        });
      } else {
        this.handleRefresh(this.periods);
      }
    },

    // Single Coin Search Methods
    async handleSingleCoinSearch(payload) {
      const { coin, period } = payload;
      this.singleSearchCoin = coin.coinName;
      this.singleSearchPeriod = period;
      this.singleCoinSearchActive = true;


      const loading = this.$loading.show();

      try {
        // Fetch coin metrics
        await this.fetchSingleCoinMetrics(coin.coinName);

        // Fetch chart data based on selected metrics
        const jobs = [];
        const sel = new Set(this.effectiveSelection);
        if (sel.has('openInterest')) jobs.push(this.fetchSingleCoinNotionalData(coin.coinName, period));
        if (sel.has('longShort')) {
          jobs.push(
            this.fetchSingleCoinSecondApiData(coin.coinName, period),
            this.fetchSingleCoinThirdApiData(coin.coinName, period),
            this.fetchSingleCoinFourApiData(coin.coinName, period)
          );
        }
        if (this.hasSpotMarket(coin.coinName)) {
          if (sel.has('inflow30d')) jobs.push(this.fetchSingleCoinFiveApiData(coin.coinName));
          if (sel.has('inflow24h')) jobs.push(this.fetchSingleCoinSixApiData(coin.coinName, period));
        } else {
          this.singleSearchFiveChartDataSets = {
            dataSet: [],
            totalInFlow: 0,
            totalInFlowUsdt: 0,
            dates: [],
          };
          this.singleSearchSixChartDataSets = [];
        }
        if (sel.has('fundingRate')) jobs.push(this.fetchSingleCoinFundingRateData(coin.coinName, period));

        await Promise.all(jobs);

        // Create charts
        this.createSingleCoinCharts();
      } catch (error) {
        console.error('Error fetching single coin data:', error);
        this.$toast.open({
          message: 'Error loading coin data!',
          type: 'error',
          position: 'top-right',
        });
      } finally {
        loading.hide();
      }
    },

    handleClearSingleCoinSearch() {
      this.singleCoinSearchActive = false;
      this.singleSearchCoin = "";
      this.singleSearchPeriod = "";
      this.isSingleCoinMetricsLoading = false;
      this.singleCoinMetrics = { marketcap: null, fdv: null, volMktCapPercent: null, oiMktCapPercent: null, cmcUrl: null, xLink: null, monitoringFlag: null };

      // Destroy all single search charts
      const killChart = (c) => { try { c && !c._destroyed && c.destroy(); } catch (_) { /* ignore */ } };
      [this.singleSearchCharts, this.singleSearchSecondCharts, this.singleSearchThirdCharts,
      this.singleSearchFourCharts, this.singleSearchFiveCharts, this.singleSearchSixCharts,
      this.singleSearchSevenCharts, this.singleSearchEightCharts].forEach(arr => {
        (arr || []).forEach(c => killChart(c));
      });

      // Clear chart arrays
      this.singleSearchCharts = [];
      this.singleSearchSecondCharts = [];
      this.singleSearchThirdCharts = [];
      this.singleSearchFourCharts = [];
      this.singleSearchFiveCharts = [];
      this.singleSearchSixCharts = [];
      this.singleSearchSevenCharts = [];
      this.singleSearchEightCharts = [];
    },

    async fetchSingleCoinMetrics(coinName) {
      this.isSingleCoinMetricsLoading = true;
      this.singleCoinMetrics = { marketcap: null, fdv: null, volMktCapPercent: null, oiMktCapPercent: null, cmcUrl: null, xLink: null, monitoringFlag: null };
      try {
        const cleanCoinName = coinName.replace("USDT", "");

        // Call API binance/get-mkc-fdm
        const data = await BinanceService.getMkcFdm([cleanCoinName]);

        // Find monitoring flag from coins list
        const coinNameWithUSDT = coinName.endsWith('USDT') ? coinName : `${coinName}USDT`;
        const coinInfo = this.coins.find(coin => coin.coinName === coinNameWithUSDT);
        const monitoringFlag = coinInfo ? coinInfo.monitoringFlag : null;
        this.singleCoinMetrics.monitoringFlag = monitoringFlag;

        if (data && data.length > 0) {
          const newVal = data[0].volMktCapPercent;
          let effectiveCmcUrl = data[0].cmcUrl;
          if (cleanCoinName === "1000CAT" && (!effectiveCmcUrl || effectiveCmcUrl.includes("caterpillar"))) {
            effectiveCmcUrl = "https://coinmarketcap.com/currencies/simonscat/";
          }
          this.singleCoinMetrics = {
            marketcap: data[0].marketcap,
            fdv: data[0].fdv,
            volMktCapPercent: newVal,
            oiMktCapPercent: data[0].oiMktCapPercent,
            cmcUrl: effectiveCmcUrl,
            xLink: data[0].xLink,
            monitoringFlag: monitoringFlag,
          };

          // Helper chuẩn hóa symbol để tìm kiếm chính xác 100% không lệch hoa thường, khoảng trắng hay tên đầy đủ
          const normalizeSymbol = (sym) => {
            if (!sym) return '';
            const firstPart = String(sym).trim().split(' ')[0];
            return firstPart.toUpperCase().replace("USDT", "");
          };

          const targetClean = normalizeSymbol(coinName);
          console.log(`[Sync] Bắt đầu đồng bộ Vol/Mc cho coin ${coinName} (chuẩn hóa: ${targetClean}) với giá trị: ${newVal}`);

          // Cập nhật ở cha (Binance.vue)
          const parentCoin = this.coins.find(c => normalizeSymbol(c.coinName) === targetClean);
          if (parentCoin) {
            parentCoin.volmc = newVal;
            console.log(`[Sync] Đã cập nhật ở cha cho coin: ${parentCoin.coinName}`);
          }

          // Cập nhật ở con (FavoriteBinance.vue) thông qua ngữ cảnh của con
          if (this.$refs.favoriteBinance) {
            const childCoin = this.$refs.favoriteBinance.coins.find(c => normalizeSymbol(c.coinName) === targetClean);
            if (childCoin) {
              childCoin.volmc = newVal;
              console.log(`[Sync] Đã cập nhật ở danh sách coins của con cho coin: ${childCoin.coinName}`);
            }
            const childFilteredCoin = this.$refs.favoriteBinance.filteredCoins.find(c => normalizeSymbol(c.coinName) === targetClean);
            if (childFilteredCoin) {
              childFilteredCoin.volmc = newVal;
              console.log(`[Sync] Đã cập nhật ở danh sách filteredCoins của con cho coin: ${childFilteredCoin.coinName}`);
            }
            // Ép buộc con phải re-render ngay lập tức để cập nhật UI danh sách kết quả
            this.$refs.favoriteBinance.$forceUpdate();
          }
        }
      } catch (error) {
        console.error(`Error fetching coin metrics for ${coinName}:`, error?.message || error);
      } finally {
        this.isSingleCoinMetricsLoading = false;
      }
    },

    // Fetch Opent Interest without index
    async fetchSingleCoinNotionalData(coinName, period) {
      try {
        const chartData = await this.getNotionalChartData(coinName, period);
        this.singleSearchChartDataSets = chartData;

        if (chartData && chartData.length > 0) {
          const lastPoint = chartData[chartData.length - 1];
          if (lastPoint && lastPoint.notional !== undefined) {
            const newVal = lastPoint.notional;

            const normalizeSymbol = (sym) => {
              if (!sym) return '';
              const firstPart = String(sym).trim().split(' ')[0];
              return firstPart.toUpperCase().replace("USDT", "");
            };

            const targetClean = normalizeSymbol(coinName);
            console.log(`[Sync OI] Bắt đầu đồng bộ Open Interest cho coin ${coinName} (chuẩn hóa: ${targetClean}) với giá trị: ${newVal}`);

            const parentCoin = this.coins.find(c => normalizeSymbol(c.coinName) === targetClean);
            if (parentCoin) {
              parentCoin.openInterest = newVal;
              console.log(`[Sync OI] Đã cập nhật ở cha cho coin: ${parentCoin.coinName}`);
            }

            if (this.$refs.favoriteBinance) {
              const childCoin = this.$refs.favoriteBinance.coins.find(c => normalizeSymbol(c.coinName) === targetClean);
              if (childCoin) {
                childCoin.openInterest = newVal;
                console.log(`[Sync OI] Đã cập nhật ở danh sách coins của con cho coin: ${childCoin.coinName}`);
              }
              const childFilteredCoin = this.$refs.favoriteBinance.filteredCoins.find(c => normalizeSymbol(c.coinName) === targetClean);
              if (childFilteredCoin) {
                childFilteredCoin.openInterest = newVal;
                console.log(`[Sync OI] Đã cập nhật ở danh sách filteredCoins của con cho coin: ${childFilteredCoin.coinName}`);
              }
              this.$refs.favoriteBinance.$forceUpdate();
            }
          }
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API cho ${coinName}:`, error?.message || error);
        this.singleSearchChartDataSets = [];
      }
    },

    async fetchSingleCoinSecondApiData(coinName, period) {
      try {
        const chartData = await this.getLongShortChartData(
          (payload) => BinanceService.getLongshortAcc(payload),
          coinName,
          period
        );
        this.singleSearchSecondChartDataSets = chartData;
        if (chartData && chartData.length > 0) {
          this.syncRatioToChild(coinName, 'topAccounts', chartData[chartData.length - 1].longShortRatio);
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API cho ${coinName}:`, error.message || error);
        this.singleSearchSecondChartDataSets = [];
      }
    },

    async fetchSingleCoinThirdApiData(coinName, period) {
      try {
        const chartData = await this.getLongShortChartData(
          (payload) => BinanceService.getLongshortPoss(payload),
          coinName,
          period
        );
        this.singleSearchThirdChartDataSets = chartData;
        if (chartData && chartData.length > 0) {
          this.syncRatioToChild(coinName, 'topPositions', chartData[chartData.length - 1].longShortRatio);
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API cho ${coinName}:`, error.message || error);
        this.singleSearchThirdChartDataSets = [];
      }

    },

    async fetchSingleCoinFourApiData(coinName, period) {
      try {
        const chartData = await this.getLongShortChartData(
          (payload) => BinanceService.getLongshort(payload),
          coinName,
          period
        );
        this.singleSearchFourChartDataSets = chartData;
        if (chartData && chartData.length > 0) {
          this.syncRatioToChild(coinName, 'globalAccounts', chartData[chartData.length - 1].longShortRatio);
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API mới cho ${coinName}:`, error.message || error);
        this.singleSearchFourChartDataSets = [];
      }

    },


    async fetchSingleCoinFiveApiData(coinName) {
      try {
        this.singleSearchFiveChartDataSets =
          await this.getDayLargeInflowChartData(coinName);
        if (isAllZeroArray(this.singleSearchFiveChartDataSets.dataSet)) {
          this.singleSearchFiveChartDataSets.dataSet = []
        }
      } catch (error) {
        console.error(`Lỗi khi tải dữ liệu từ API cho ${coinName}:`, error?.message || error);

        this.singleSearchFiveChartDataSets = {
          dataSet: Array(this.inflowDays).fill(0),
          totalInFlow: 0,
          totalInFlowUsdt: 0,
        };
      }
    },

    async fetchSingleCoinSixApiData(coinName, period) {
      try {
        this.singleSearchSixChartDataSets =
          await this.get24HourLargeInflowChartData(coinName, period);
      } catch (error) {
        this.singleSearchSixChartDataSets = [];
        console.error(
          `Lỗi khi tải dữ liệu từ API cho ${coinName}:`,
          error.message || error
        );
      }
    },

    async fetchSingleCoinFundingRateData(coinName, period) {
      try {
        const { fundingRateResult, ratioResult } =
          await this.getFundingRateChartData(coinName, period);

        this.singleSearchSevenChartDataSets = fundingRateResult;
        this.singleSearchEightChartDataSets = ratioResult;
      } catch (error) {
        this.singleSearchSevenChartDataSets = [];
        this.singleSearchEightChartDataSets = [];

        console.error(
          `Lỗi khi tải dữ liệu Funding Rate cho ${coinName}:`,
          error.message || error
        );
      }
    },

    createSingleCoinCharts() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          // Destroy existing single search charts
          const kill = c => { try { c && !c._destroyed && c.destroy(); } catch (_) { /* ignore */ } };
          [this.singleSearchCharts, this.singleSearchSecondCharts, this.singleSearchThirdCharts,
          this.singleSearchFourCharts, this.singleSearchFiveCharts, this.singleSearchSixCharts,
          this.singleSearchSevenCharts, this.singleSearchEightCharts].forEach(arr => {
            (arr || []).forEach((c, i) => {
              const cv = c?.canvas;
              if (!cv || !document.body.contains(cv)) { kill(c); arr[i] = null; }
            });
          });

          const periods = this.singleSearchPeriod;
          const coinName = this.singleSearchCoin;

          // Create Open Interest Chart (Chart 1)
          if (this.effectiveSelection.includes('openInterest')) {
            const canvas = document.getElementById('single-search-chart-0');
            if (canvas && canvas.offsetParent !== null) {
              const exist = ChartJS.getChart(canvas);
              if (exist) { try { exist.destroy(); } catch (e) { /* skip */ } }

              const ctx = canvas.getContext("2d");
              if (ctx) {
                const dataSet = this.singleSearchChartDataSets || [];
                const labels = dataSet.map(item => item.x);
                const oi = dataSet.map(item => item.oi);
                const notional = dataSet.map(item => item.notional);

                const yL = niceMinMax(oi, 0.06);
                const yR = niceMinMax(notional, 0.06);
                const LEFT_TICKS = 4;
                const RIGHT_TICKS = 4;
                const leftStep = (yL.max - yL.min) / Math.max(1, LEFT_TICKS - 1);
                const rightStep = (yR.max - yR.min) / Math.max(1, RIGHT_TICKS - 1);

                this.singleSearchCharts[0] = new ChartJS(ctx, {
                  type: "bar",
                  data: {
                    labels,
                    datasets: [
                      {
                        type: 'bar',
                        label: `Open Interest (${coinName.replace('USDT', '')})`,
                        data: oi,
                        yAxisID: 'y',
                        backgroundColor: '#f0b90b',
                        borderColor: '#dca600',
                        borderSkipped: false,
                        borderWidth: 0,
                        borderRadius: 2,
                        barPercentage: 0.6,
                        categoryPercentage: 0.8,
                        maxBarThickness: 12,
                        datalabels: { display: false },
                        order: 1
                      },
                      {
                        type: 'line',
                        label: 'Notional Value of Open Interest (USDT)',
                        data: notional,
                        yAxisID: 'y1',
                        borderColor: '#ffffff',
                        borderWidth: 2,
                        order: 999,
                        z: 10,
                        showLine: true,
                        spanGaps: true,
                        pointRadius: 2,
                        pointHoverRadius: 3,
                        pointBackgroundColor: '#0b1220',
                        pointBorderColor: '#e5e7eb',
                        pointBorderWidth: 1,
                        tension: 0.25,
                        cubicInterpolationMode: 'monotone',
                        fill: false,
                        datalabels: { display: false }
                      }
                    ],
                  },
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: 0 },
                    interaction: { mode: 'index', intersect: false },
                    plugins: {
                      legend: { display: true, reverse: false },
                      datalabels: { display: false },
                      title: { display: true, text: `Open Interest(${coinName})` },
                      tooltip: {
                        backgroundColor: '#111827',
                        borderColor: '#374151',
                        borderWidth: 1,
                        titleColor: '#e5e7eb',
                        bodyColor: '#e5e7eb',
                        callbacks: {
                          title(items) {
                            const ts = items[0]?.parsed.x;
                            return ts ? new Date(ts).toLocaleTimeString() : '';
                          },
                          label(c) {
                            const name = c.dataset.label;
                            const value = c.parsed.y;
                            if (name.includes('Notional') || name.includes('Value')) {
                              return ` ${name}: ${formatNumber(value)}`;
                            }
                            return ` ${name}: ${value % 1 === 0 ? fmtComma(value) : value.toFixed(2)}`;
                          }
                        }
                      },
                    },
                    scales: {
                      x: {
                        type: 'time',
                        time: {
                          unit: periods === "1h" || periods === "4h" ? "hour"
                            : periods === "1d" ? "day" : "minute"
                        },
                        grid: { color: 'rgba(255,255,255,0.10)', drawTicks: true, drawOnChartArea: true },
                        offset: false,
                        bounds: 'data',
                        ticks: {
                          color: '#9ca3af',
                          maxTicksLimit: 15,
                          count: 15,
                          autoSkip: true,
                          autoSkipPadding: 8,
                          maxRotation: 55,
                          minRotation: 35,
                          padding: 2,
                          callback: function (value) {
                            const date = new Date(value);
                            if (periods === "1d") {
                              return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
                            } else {
                              return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                            }
                          },
                        },
                      },
                      y: {
                        position: 'left',
                        min: yL.min,
                        max: yL.max,
                        beginAtZero: false,
                        grid: { color: 'rgba(255,255,255,0.10)', drawOnChartArea: true, drawTicks: true },
                        ticks: {
                          color: '#9ca3af', font: { size: 11 }, padding: 0, count: LEFT_TICKS, stepSize: leftStep,
                          callback: function (v) {
                            const a = Math.abs(v);
                            if (a >= 1e12) return (v / 1e12).toFixed(2) + 'T';
                            if (a >= 1e9) return (v / 1e9).toFixed(2) + 'B';
                            if (a >= 1e6) return (v / 1e6).toFixed(2) + 'M';
                            if (a >= 1e3) return (v / 1e3).toFixed(2) + 'K';
                            return v % 1 === 0 ? v : v.toFixed(2);
                          }
                        },
                        title: { display: false }
                      },
                      y1: {
                        position: 'right',
                        min: yR.min,
                        max: yR.max,
                        beginAtZero: false,
                        grid: { display: false, drawOnChartArea: false },
                        ticks: {
                          color: '#9ca3af', font: { size: 11 }, padding: 0, count: RIGHT_TICKS, stepSize: rightStep,
                          callback: function (v) {
                            const a = Math.abs(v);
                            if (a >= 1e12) return (v / 1e12).toFixed(2) + 'T';
                            if (a >= 1e9) return (v / 1e9).toFixed(2) + 'B';
                            if (a >= 1e6) return (v / 1e6).toFixed(2) + 'M';
                            if (a >= 1e3) return (v / 1e3).toFixed(2) + 'K';
                            return v % 1 === 0 ? v : v.toFixed(2);
                          }
                        },
                        title: { display: false }
                      }
                    }
                  },
                  plugins: [stripeBg]
                });
                if (canvas && canvas.parentNode) {
                  canvas.parentNode.style.height = '300px';
                }
              }
            }
          }

          // Create Long/Short Account Ratio Chart (Chart 2)
          if (this.effectiveSelection.includes('longShort')) {
            const canvas2 = document.getElementById('single-search-second-chart-0');
            if (canvas2 && canvas2.offsetParent !== null) {
              const exist2 = ChartJS.getChart(canvas2);
              if (exist2) { try { exist2.destroy(); } catch (e) { /* skip */ } }

              const ctx2 = canvas2.getContext("2d");
              if (ctx2) {
                const dataSet = this.singleSearchSecondChartDataSets || [];
                const minValue = Math.min(...dataSet.map((item) => item.longShortRatio));
                const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                const startValue = minValue - roundedValue <= 0.05 ? roundedValue : roundedValue + 0.05;

                this.singleSearchSecondCharts[0] = new ChartJS(ctx2, {
                  type: "bar",
                  data: {
                    datasets: [
                      {
                        label: "Tỷ lệ Long/Short (Tài khoản)",
                        type: "line",
                        data: dataSet,
                        borderColor: "rgba(0, 0, 0, 1)",
                        pointBackgroundColor: "rgba(255, 255, 255, 1)",
                        pointBorderColor: "rgba(255, 255, 255, 0.7)",
                        pointBorderWidth: 0.2,
                        pointRadius: 2,
                        fill: true,
                        tension: 0.4,
                        borderWidth: 2,
                        yAxisID: "y-line",
                        meta: { customValue: dataSet },
                      },
                      {
                        label: "% Tài khoản Long",
                        data: dataSet.map((item) => item.longPercent),
                        backgroundColor: "rgba(0, 209, 112, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: { customValue: dataSet },
                      },
                      {
                        label: "% Tài khoản Short",
                        data: dataSet.map((item) => item.shortPercent),
                        backgroundColor: "rgba(231, 76, 60, 0.8)",
                        stack: "Stack 0",
                        yAxisID: "y-bar",
                        meta: { customValue: dataSet },
                      },
                    ],
                  },
                  options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: true },
                      datalabels: { display: false },
                      title: { display: true, text: `Top Accounts(${coinName})` },
                      tooltip: {
                        callbacks: {
                          label: function (tooltipItem) {
                            const dataset = tooltipItem.dataset;
                            const rawData = dataset.meta.customValue;
                            const index = tooltipItem.dataIndex;
                            const shortPercent = rawData[index].shortPercent || 0;
                            const longPercent = rawData[index].longPercent || 0;
                            const longShortRatio = rawData[index].longShortRatio || 0;
                            return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        type: "time",
                        time: {
                          ...(periods === "1d" && { round: "day" }),
                          unit: periods === "1h" || periods === "4h" ? "hour" : periods === "1d" ? "day" : "minute",
                        },
                        labels: dataSet.map((item) => item.x),
                        ticks: {
                          maxTicksLimit: 20,
                          ...(periods !== "1d" && {
                            stepSize: periods === "1h" ? 1 : periods === "4h" ? 4 : 5,
                          }),
                          callback: function (value) {
                            const date = new Date(value);
                            if (periods === "1d") {
                              return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
                            } else {
                              return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                            }
                          },
                        },
                      },
                      "y-bar": {
                        type: "linear",
                        position: "left",
                        stacked: true,
                        min: 0,
                        max: 100,
                        ticks: { stepSize: 20 },
                      },
                      "y-line": {
                        type: "linear",
                        position: "right",
                        min: startValue,
                        grid: { drawOnChartArea: false },
                        ticks: {
                          callback: function (value) {
                            return value.toFixed(2);
                          },
                        },
                      },
                    },
                  },
                  plugins: [stripeBg]
                });
                if (canvas2 && canvas2.parentNode) {
                  canvas2.parentNode.style.height = '300px';
                }
              }
            }
          }

          // Create Long/Short Position Ratio Chart (Chart 3)
          if (this.effectiveSelection.includes('longShort')) {
            const canvas3 = document.getElementById('single-search-third-chart-0');
            if (canvas3 && canvas3.offsetParent !== null) {
              const exist3 = ChartJS.getChart(canvas3);
              if (exist3) {
                try { exist3.destroy(); } catch (e) { /* skip */ }
              }
              const ctx3 = canvas3.getContext('2d');
              if (ctx3) {
                if (this.singleSearchThirdCharts[0]) {
                  try { this.singleSearchThirdCharts[0].destroy(); } catch (e) { /* skip */ }
                  this.singleSearchThirdCharts[0] = null;
                }

                const dataSet = this.singleSearchThirdChartDataSets || [];
                if (Array.isArray(dataSet) && dataSet.length > 0) {
                  const minValue = Math.min(...dataSet.map((item) => item.longShortRatio));
                  const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                  const startValue = minValue - roundedValue <= 0.05 ? roundedValue : roundedValue + 0.05;

                  this.singleSearchThirdCharts[0] = new ChartJS(ctx3, {
                    type: "bar",
                    data: {
                      datasets: [
                        {
                          label: "Tỷ lệ Long/Short (Vị thế)",
                          type: "line",
                          data: dataSet,
                          borderColor: "rgba(0, 0, 0, 1)",
                          pointBackgroundColor: "rgba(255, 255, 255, 1)",
                          pointBorderColor: "rgba(255, 255, 255, 0.7)",
                          pointBorderWidth: 0.2,
                          pointRadius: 2,
                          fill: true,
                          tension: 0.4,
                          borderWidth: 2,
                          yAxisID: "y-line",
                          meta: { customValue: dataSet },
                        },
                        {
                          label: "% Vị thế Long",
                          data: dataSet.map((item) => item.longPercent),
                          backgroundColor: "rgba(0, 209, 112, 0.8)",
                          stack: "Stack 0",
                          yAxisID: "y-bar",
                          meta: { customValue: dataSet },
                        },
                        {
                          label: "% Vị thế Short",
                          data: dataSet.map((item) => item.shortPercent),
                          backgroundColor: "rgba(231, 76, 60, 0.8)",
                          stack: "Stack 0",
                          yAxisID: "y-bar",
                          meta: { customValue: dataSet },
                        },
                      ],
                    },
                    options: {
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true },
                        datalabels: { display: false },
                        title: { display: true, text: `Top Positions(${coinName})` },
                        tooltip: {
                          callbacks: {
                            label: function (tooltipItem) {
                              const dataset = tooltipItem.dataset;
                              const rawData = dataset.meta.customValue;
                              const index = tooltipItem.dataIndex;
                              const shortPercent = rawData[index].shortPercent || 0;
                              const longPercent = rawData[index].longPercent || 0;
                              const longShortRatio = rawData[index].longShortRatio || 0;
                              return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          type: "time",
                          time: {
                            ...(periods === "1d" && { round: "day" }),
                            unit: periods === "1h" || periods === "4h" ? "hour" : periods === "1d" ? "day" : "minute",
                          },
                          labels: dataSet.map((item) => item.x),
                          ticks: {
                            maxTicksLimit: 20,
                            ...(periods !== "1d" && {
                              stepSize: periods === "1h" ? 1 : periods === "4h" ? 4 : 5,
                            }),
                            callback: function (value) {
                              const date = new Date(value);
                              if (periods === "1d") {
                                return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
                              } else {
                                return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                              }
                            },
                          },
                          stacked: true,
                        },
                        "y-bar": {
                          type: "linear",
                          position: "left",
                          stacked: true,
                          min: 0,
                          max: 100,
                          ticks: { stepSize: 20 },
                        },
                        "y-line": {
                          type: "linear",
                          position: "right",
                          min: startValue,
                          grid: { drawOnChartArea: false },
                          ticks: {
                            callback: function (value) {
                              return value.toFixed(2);
                            },
                          },
                        },
                      },
                    },
                    plugins: [stripeBg]
                  });
                  if (canvas3 && canvas3.parentNode) {
                    canvas3.parentNode.style.height = '300px';
                  }
                }
              }
            }
          }

          // Create Long/Short Global Ratio Chart (Chart 4)
          if (this.effectiveSelection.includes('longShort')) {
            const canvas4 = document.getElementById('single-search-four-chart-0');
            if (canvas4 && canvas4.offsetParent !== null) {
              const exist4 = ChartJS.getChart(canvas4);
              if (exist4) {
                try { exist4.destroy(); } catch (e) { /* skip */ }
              }
              const ctx4 = canvas4.getContext('2d');
              if (ctx4) {
                if (this.singleSearchFourCharts[0]) {
                  try { this.singleSearchFourCharts[0].destroy(); } catch (e) { /* skip */ }
                  this.singleSearchFourCharts[0] = null;
                }

                const dataSet = this.singleSearchFourChartDataSets || [];
                if (Array.isArray(dataSet) && dataSet.length > 0) {
                  const minValue = Math.min(...dataSet.map((item) => item.longShortRatio));
                  const roundedValue = Math.floor(minValue / 0.05) * 0.05;
                  const startValue = minValue - roundedValue <= 0.05 ? roundedValue : roundedValue + 0.05;

                  this.singleSearchFourCharts[0] = new ChartJS(ctx4, {
                    type: "bar",
                    data: {
                      datasets: [
                        {
                          label: "Tỷ lệ Long/Short",
                          type: "line",
                          data: dataSet,
                          borderColor: "rgba(0, 0, 0, 1)",
                          pointBackgroundColor: "rgba(255, 255, 255, 1)",
                          pointBorderColor: "rgba(255, 255, 255, 0.7)",
                          pointBorderWidth: 0.2,
                          pointRadius: 2,
                          fill: true,
                          tension: 0.4,
                          borderWidth: 2,
                          yAxisID: "y-line",
                          meta: { customValue: dataSet },
                        },
                        {
                          label: "% Tài khoản Long",
                          data: dataSet.map((item) => item.longPercent),
                          backgroundColor: "rgba(0, 209, 112, 0.8)",
                          stack: "Stack 0",
                          yAxisID: "y-bar",
                          meta: { customValue: dataSet },
                        },
                        {
                          label: "% Tài khoản Short",
                          data: dataSet.map((item) => item.shortPercent),
                          backgroundColor: "rgba(231, 76, 60, 0.8)",
                          stack: "Stack 0",
                          yAxisID: "y-bar",
                          meta: { customValue: dataSet },
                        },
                      ],
                    },
                    options: {
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: true },
                        datalabels: { display: false },
                        title: { display: true, text: `Accounts(${coinName})` },
                        tooltip: {
                          callbacks: {
                            label: function (tooltipItem) {
                              const dataset = tooltipItem.dataset;
                              const rawData = dataset.meta.customValue;
                              const index = tooltipItem.dataIndex;
                              const shortPercent = rawData[index].shortPercent || 0;
                              const longPercent = rawData[index].longPercent || 0;
                              const longShortRatio = rawData[index].longShortRatio || 0;
                              return `Long: ${longPercent}%\r\nShort: ${shortPercent}%\r\nLong Short Ratio: ${Number(longShortRatio).toFixed(3)}`;
                            },
                          },
                        },
                      },
                      scales: {
                        x: {
                          type: "time",
                          time: {
                            ...(periods === "1d" && { round: "day" }),
                            unit: periods === "1h" || periods === "4h" ? "hour" : periods === "1d" ? "day" : "minute",
                          },
                          labels: dataSet.map((item) => item.x),
                          ticks: {
                            maxTicksLimit: 20,
                            ...(periods !== "1d" && {
                              stepSize: periods === "1h" ? 1 : periods === "4h" ? 4 : 5,
                            }),
                            callback: function (value) {
                              const date = new Date(value);
                              if (periods === "1d") {
                                return date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit" });
                              } else {
                                return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
                              }
                            },
                          },
                          stacked: true,
                        },
                        "y-bar": {
                          type: "linear",
                          position: "left",
                          stacked: true,
                          min: 0,
                          max: 100,
                          ticks: { stepSize: 20 },
                        },
                        "y-line": {
                          type: "linear",
                          position: "right",
                          min: startValue,
                          grid: { drawOnChartArea: false },
                          ticks: {
                            callback: function (value) {
                              return value.toFixed(2);
                            },
                          },
                        },
                      },
                    },
                    plugins: [stripeBg]
                  });
                  if (canvas4 && canvas4.parentNode) {
                    canvas4.parentNode.style.height = '300px';
                  }
                }
              }
            }
          }

          // Create 30-day Large Inflow Chart (Chart 5)
          if (this.effectiveSelection.includes('inflow30d')) {
            const canvas5 = document.getElementById('single-search-five-chart-0');
            if (canvas5 && canvas5.offsetParent !== null) {
              const exist5 = ChartJS.getChart(canvas5);
              if (exist5) {
                try { exist5.destroy(); } catch (e) { /* skip */ }
              }
              const ctx5 = canvas5.getContext('2d');
              if (ctx5) {
                if (this.singleSearchFiveCharts[0]) {
                  try { this.singleSearchFiveCharts[0].destroy(); } catch (e) { /* skip */ }
                  this.singleSearchFiveCharts[0] = null;
                }

                const dataGet = this.singleSearchFiveChartDataSets || {};
                const dataSet = dataGet.dataSet || [];
                if (Array.isArray(dataSet) && dataSet.length > 0) {
                  const totalInFlow = Math.abs(dataGet.totalInFlow || 0);
                  let dataTotalInFlow = 0;
                  if (totalInFlow >= 1000000000) {
                    dataTotalInFlow = (totalInFlow / 1000000000).toFixed(2) + "B";
                  } else if (totalInFlow >= 1000000) {
                    dataTotalInFlow = (totalInFlow / 1000000).toFixed(2) + "M";
                  } else if (totalInFlow >= 1000) {
                    dataTotalInFlow = (totalInFlow / 1000).toFixed(2) + "K";
                  }

                  const totalInFlowUsdt = Math.abs(dataGet.totalInFlowUsdt || 0);
                  let dataTotalInFlowUsdt = 0;
                  if (totalInFlowUsdt >= 1000000000) {
                    dataTotalInFlowUsdt = (totalInFlowUsdt / 1000000000).toFixed(2) + "B";
                  } else if (totalInFlowUsdt >= 1000000) {
                    dataTotalInFlowUsdt = (totalInFlowUsdt / 1000000).toFixed(2) + "M";
                  } else if (totalInFlowUsdt >= 1000) {
                    dataTotalInFlowUsdt = (totalInFlowUsdt / 1000).toFixed(2) + "K";
                  }

                  this.singleSearchFiveCharts[0] = new ChartJS(ctx5, {
                    type: "bar",
                    data: {
                      labels: dataGet.dates || Array.from({ length: this.inflowDays }, (_, i) => i + 1),
                      datasets: [
                        {
                          label: `${this.inflowDays}d Trading Flow: ${dataTotalInFlow} || ${dataTotalInFlowUsdt}`,
                          data: dataSet,
                          backgroundColor: dataSet.map((value) =>
                            value < 0 ? "rgba(255, 0, 0, 0.6)" : "rgba(0, 128, 0, 0.6)"
                          ),
                          borderColor: dataSet.map((value) =>
                            value < 0 ? "rgb(255, 0, 0)" : "rgb(0, 128, 0)"
                          ),
                          borderWidth: 1,
                        },
                      ],
                    },
                    options: {
                      interaction: {
                        mode: "index",
                        intersect: false,
                      },
                      maintainAspectRatio: false,
                      responsive: true,
                      scales: {
                        x: {
                          display: false,
                          reverse: true
                        },
                        y: {
                          display: true,
                          ticks: {
                            callback: function (value) {
                              const absValue = Math.abs(value);
                              if (absValue >= 1000000000) {
                                return (absValue / 1000000000).toFixed(2) + "B";
                              } else if (absValue >= 1000000) {
                                return (absValue / 1000000).toFixed(2) + "M";
                              } else if (absValue >= 1000) {
                                return (absValue / 1000).toFixed(2) + "K";
                              }
                              return absValue;
                            },
                          },
                        },
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: `${this.inflowDays}d Trading Flow: ${dataTotalInFlow} || ${dataTotalInFlowUsdt}`,
                          font: { style: "italic" },
                          color: (dataGet.totalInFlow || 0) < 0 ? "red" : "green",
                          padding: 30,
                        },
                        tooltip: {
                          enabled: true,
                          callbacks: {
                            title: function (tooltipItems) {
                              return `Thời gian: ${tooltipItems[0].label}`;
                            },
                            label: function (context) {
                              const value = context.raw;
                              const absValue = Math.abs(value);
                              let formattedValue = value;
                              if (absValue >= 1000000000) {
                                formattedValue = (value / 1000000000).toFixed(2) + "B";
                              } else if (absValue >= 1000000) {
                                formattedValue = (value / 1000000).toFixed(2) + "M";
                              } else if (absValue >= 1000) {
                                formattedValue = (value / 1000).toFixed(2) + "K";
                              }
                              return `Giá trị: ${formattedValue}`;
                            }
                          }
                        },
                        legend: { display: false },
                        datalabels: {
                          display: true,
                          anchor: "end",
                          align: "top",
                          color: function (context) {
                            return context.dataset.data[context.dataIndex] < 0 ? "red" : "green";
                          },
                          font: {
                            weight: "bold",
                            size: 10,
                          },
                          formatter: function (value) {
                            const absValue = Math.abs(value);
                            if (value === 0) return "0";
                            if (absValue >= 1000000000) {
                              return (value / 1000000000).toFixed(2) + "B";
                            } else if (absValue >= 1000000) {
                              return (value / 1000000).toFixed(2) + "M";
                            } else if (absValue >= 1000) {
                              return (value / 1000).toFixed(2) + "K";
                            }
                            return value;
                          },
                        },
                      },
                    },
                  });
                  if (canvas5 && canvas5.parentNode) {
                    canvas5.parentNode.style.height = '300px';
                  }
                }
              }
            }
          }

          // Create 24-hour Large Inflow Chart (Chart 6)
          if (this.effectiveSelection.includes('inflow24h')) {
            console.log('Creating Chart 6 - Trading Flow, data:', this.singleSearchSixChartDataSets);
            const canvas6 = document.getElementById('single-search-six-chart-0');
            if (canvas6 && canvas6.offsetParent !== null) {
              const exist6 = ChartJS.getChart(canvas6);
              if (exist6) {
                try { exist6.destroy(); } catch (e) { /* skip */ }
              }
              const ctx6 = canvas6.getContext('2d');
              if (ctx6) {
                if (this.singleSearchSixCharts[0]) {
                  try { this.singleSearchSixCharts[0].destroy(); } catch (e) { /* skip */ }
                  this.singleSearchSixCharts[0] = null;
                }
                const dataSet = this.singleSearchSixChartDataSets || [];
                if (Array.isArray(dataSet) && dataSet.length > 0) {
                  this.singleSearchSixCharts[0] = new ChartJS(ctx6, {
                    type: "line",
                    data: {
                      datasets: [
                        {
                          data: dataSet,
                          borderColor: "#f39c12",
                          backgroundColor: "rgba(243, 156, 18, 0.5)",
                          fill: true,
                          tension: 0,
                          borderWidth: 2,
                          pointRadius: 0,
                        },
                      ],
                    },
                    options: {
                      interaction: {
                        mode: "index",
                        intersect: false,
                      },
                      plugins: {
                        datalabels: { display: false },
                        legend: { display: false },
                        title: {
                          display: true,
                          text: `Trading Flow(${coinName})`,
                        },
                        tooltip: { enabled: true },
                      },
                      scales: {
                        x: {
                          type: "time",
                          grid: { display: false },
                          time: { unit: "hour" },
                          labels: dataSet.map((item) => item.x),
                          ticks: {
                            maxTicksLimit: 30,
                            stepSize: 6,
                            callback: function (value) {
                              const date = new Date(value);
                              const time = date.toLocaleTimeString("en-GB", {
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                              return time;
                            },
                          },
                          stacked: true,
                        },
                        y: { display: true },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                    },
                  });
                  if (canvas6 && canvas6.parentNode) {
                    canvas6.parentNode.style.height = '300px';
                  }
                }
              }
            }
          }

          // Create Funding Rate Chart (Chart 7)
          if (this.effectiveSelection.includes('fundingRate')) {
            const canvas7 = document.getElementById('single-search-seven-chart-0');
            if (canvas7 && canvas7.offsetParent !== null) {
              const exist7 = ChartJS.getChart(canvas7);
              if (exist7) {
                try { exist7.destroy(); } catch (e) { /* skip */ }
              }
              const ctx7 = canvas7.getContext('2d');
              if (ctx7) {
                if (this.singleSearchSevenCharts[0]) {
                  try { this.singleSearchSevenCharts[0].destroy(); } catch (e) { /* skip */ }
                  this.singleSearchSevenCharts[0] = null;
                }

                const dataSet = this.singleSearchSevenChartDataSets || [];
                if (Array.isArray(dataSet) && dataSet.length > 0) {
                  this.singleSearchSevenCharts[0] = new ChartJS(ctx7, {
                    type: "line",
                    data: {
                      datasets: [
                        {
                          label: `Funding Rate (%)`,
                          data: dataSet,
                          borderColor: "#e91e63",
                          backgroundColor: "rgba(233, 30, 99, 0.25)",
                          pointBackgroundColor: "rgba(233, 30, 99, 0.5)",
                          pointBorderColor: "rgba(233, 30, 99, 0.2)",
                          pointBorderWidth: 0.2,
                          pointRadius: 2,
                          fill: true,
                          tension: 0,
                          borderWidth: 2,
                        },
                      ],
                    },
                    options: {
                      plugins: {
                        legend: { display: true },
                        datalabels: { display: false },
                        title: {
                          display: true,
                          text: `Funding Rate (${coinName})`,
                        },
                        tooltip: {
                          enabled: true,
                          callbacks: {
                            label: function (ctx) {
                              const v = ctx.parsed?.y;
                              if (v == null) return '';
                              return `Funding rate: ${Number(v).toFixed(4)}%`;
                            }
                          }
                        },
                      },
                      interaction: {
                        mode: "index",
                        intersect: false,
                      },
                      scales: {
                        x: {
                          type: "time",
                          time: {
                            ...(periods === "1d" && { round: "day" }),
                            unit: periods === "1h" || periods === "4h" ? "hour" : periods === "1d" ? "day" : "minute",
                          },
                          labels: dataSet.map((item) => item.x),
                          ticks: {
                            maxTicksLimit: 20,
                            ...(periods !== "1d" && {
                              stepSize: periods === "1h" ? 1 : periods === "4h" ? 4 : 5,
                            }),
                            callback: (val) => {
                              const ts = typeof val === 'number' ? val : Date.parse(val);
                              if (!Number.isFinite(ts)) return String(val ?? "");
                              const d = new Date(ts);

                              const tz = 'Asia/Ho_Chi_Minh';
                              if (periods === "1d") {
                                return new Intl.DateTimeFormat('en-GB', {
                                  timeZone: tz, day: '2-digit', month: '2-digit'
                                }).format(d);
                              }

                              const isHourUnit = periods === "1h" || periods === "4h" || periods === "8h";
                              return new Intl.DateTimeFormat('en-GB', {
                                timeZone: tz,
                                hour: '2-digit',
                                minute: isHourUnit ? undefined : '2-digit'
                              }).format(d);
                            },
                          },
                          stacked: true,
                        },
                        y: {
                          ticks: {
                            callback: (v) => `${Number(v).toFixed(4)}%`,
                          },
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                    },
                  });
                  if (canvas7 && canvas7.parentNode) {
                    canvas7.parentNode.style.height = '300px';
                  }
                }
              }
            }
          }

          // Create OI/Marketcap Ratio Chart (Chart 8)
          if (this.effectiveSelection.includes('oiMarketcapRatio')) {
            const canvas8 = document.getElementById('single-search-eight-chart-0');
            if (canvas8 && canvas8.offsetParent !== null) {
              const exist8 = ChartJS.getChart(canvas8);
              if (exist8) {
                try { exist8.destroy(); } catch (e) { /* skip */ }
              }
              const ctx8 = canvas8.getContext('2d');
              if (ctx8) {
                if (this.singleSearchEightCharts[0]) {
                  try { this.singleSearchEightCharts[0].destroy(); } catch (e) { /* skip */ }
                  this.singleSearchEightCharts[0] = null;
                }

                const dataSet = this.singleSearchEightChartDataSets || [];
                if (Array.isArray(dataSet) && dataSet.length > 0) {
                  this.singleSearchEightCharts[0] = new ChartJS(ctx8, {
                    type: "line",
                    data: {
                      datasets: [
                        {
                          label: `OI/Marketcap Ratio`,
                          data: dataSet,
                          borderColor: "#4ade80",
                          backgroundColor: "rgba(74, 222, 128, 0.25)",
                          fill: true,
                          tension: 0,
                          borderWidth: 2,
                        },
                      ],
                    },
                    options: {
                      plugins: {
                        legend: {
                          display: true,
                        },
                        datalabels: {
                          display: false,
                        },
                        title: {
                          display: true,
                          text: `OI/Marketcap Ratio (${this.singleSearchCoin})`,
                        },
                        tooltip: {
                          enabled: true,
                          callbacks: {
                            label: function (ctx) {
                              const v = ctx.parsed?.y;
                              if (v == null) return '';
                              return `OI/Marketcap Ratio: ${Number(v).toFixed(2)}`;
                            }
                          }
                        },
                      },
                      interaction: {
                        mode: "index",
                        intersect: false,
                      },
                      scales: {
                        x: {
                          type: "time",
                          time: {
                            ...(this.singleSearchPeriod === "1d" && {
                              round: "day",
                            }),
                            unit:
                              this.singleSearchPeriod === "1h" || this.singleSearchPeriod === "4h"
                                ? "hour"
                                : this.singleSearchPeriod === "1d"
                                  ? "day"
                                  : "minute",
                          },
                          labels: dataSet.map((item) => item.x),
                          ticks: {
                            maxTicksLimit: 20,
                            ...(this.singleSearchPeriod !== "1d" && {
                              stepSize:
                                this.singleSearchPeriod === "1h"
                                  ? 1
                                  : this.singleSearchPeriod === "4h"
                                    ? 4
                                    : 5,
                            }),
                            callback: (val) => {
                              const ts = typeof val === 'number' ? val : Date.parse(val);
                              if (!Number.isFinite(ts)) return String(val ?? "");
                              const d = new Date(ts);

                              const tz = 'Asia/Ho_Chi_Minh';
                              if (this.singleSearchPeriod === "1d") {
                                return new Intl.DateTimeFormat('en-GB', {
                                  timeZone: tz, day: '2-digit', month: '2-digit'
                                }).format(d);
                              }

                              const isHourUnit = this.singleSearchPeriod === "1h" || this.singleSearchPeriod === "4h" || this.singleSearchPeriod === "8h";
                              return new Intl.DateTimeFormat('en-GB', {
                                timeZone: tz,
                                hour: '2-digit',
                                minute: isHourUnit ? undefined : '2-digit'
                              }).format(d);
                            },
                          },
                          stacked: true,
                        },
                        y: {
                          ticks: {
                            callback: (v) => `${Number(v).toFixed(2)}`,
                          },
                        },
                      },
                      responsive: true,
                      maintainAspectRatio: false,
                    },
                  });
                  if (canvas8 && canvas8.parentNode) {
                    canvas8.parentNode.style.height = '300px';
                  }
                }
              }
            }
          }

          console.log('Single coin charts created successfully');
        });
      });
    },

    initPolling() {
      if (this.pollingTimer) clearInterval(this.pollingTimer);
      this.pollingTimer = setInterval(() => {
        this.autoRefreshData();
      }, 15000);
      console.log('⏱️ Polling for inflow24h & fundingRate initialized (15s)');

      if (this.metricsPollingTimer) clearInterval(this.metricsPollingTimer);
      this.metricsPollingTimer = setInterval(() => {
        this.fetchCoinMetrics(true);
      }, 60000);
      console.log('⏱️ Polling for coin metrics (MC, FDV, OI/MC, Vol/MC) initialized (60s)');
    },

    async autoRefreshData() {
      const hasInflow24h = this.effectiveSelection.includes('inflow24h');
      const hasFundingRate = this.effectiveSelection.includes('fundingRate') || this.effectiveSelection.includes('oiMarketcapRatio');

      if (!hasInflow24h && !hasFundingRate) return;

      console.log('🔄 Auto-refreshing data...');
      try {
        if (this.singleCoinSearchActive && this.singleSearchCoin) {
          const jobs = [];
          if (hasInflow24h) {
            jobs.push(this.fetchSingleCoinSixApiData(this.singleSearchCoin, this.singleSearchPeriod));
          }
          if (hasFundingRate) {
            jobs.push(this.fetchSingleCoinFundingRateData(this.singleSearchCoin, this.singleSearchPeriod));
          }
          await Promise.all(jobs);
          this.createSingleCoinCharts();
        } else if (this.arrayCoin && this.arrayCoin.length > 0) {
          const jobs = [];
          for (let i = 0; i < this.arrayCoin.length; i++) {
            const coin = this.arrayCoin[i];
            const coinName = typeof coin === "string" ? coin : coin.coinName;
            if (coinName) {
              if (hasInflow24h) {
                jobs.push(this.fetchSixApiDataForCoin(coinName, i, this.periods));
              }
              if (hasFundingRate) {
                jobs.push(this.fetchFundingRateDataForCoin(coinName, i));
              }
            }
          }
          if (jobs.length > 0) {
            await Promise.all(jobs);
            this.createCharts();
          }
        }
      } catch (error) {
        console.error('Error during auto-refresh:', error);
      }
    }
  },
  beforeUnmount() {
    if (this.pollingTimer) {
      clearInterval(this.pollingTimer);
      this.pollingTimer = null;
      console.log('🛑 Polling stopped');
    }
    if (this.metricsPollingTimer) {
      clearInterval(this.metricsPollingTimer);
      this.metricsPollingTimer = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      console.log('🛑 Visibility listener removed');
    }
    const killArr = (arr) => (arr || []).forEach(c => {
      try { c?.destroy(); } catch (e) {
        // skip
      }
    });
    killArr(this.charts);
    killArr(this.secondCharts);
    killArr(this.thirdCharts);
    killArr(this.fourCharts);
    killArr(this.fiveCharts);
    killArr(this.sixCharts);
    killArr(this.sevenCharts);
    killArr(this.eightCharts);

    this.charts = this.secondCharts = this.thirdCharts =
      this.fourCharts = this.fiveCharts = this.sixCharts =
      this.sevenCharts = this.eightCharts = [];
  },

  deactivated() {
    const killArr = (arr) => (arr || []).forEach(c => {
      try { c?.destroy(); } catch (e) {
        // skip
      }
    });
    killArr(this.charts);
    killArr(this.secondCharts);
    killArr(this.thirdCharts);
    killArr(this.fourCharts);
    killArr(this.fiveCharts);
    killArr(this.sixCharts);
  }
};
</script>
